(() => {
  if (window.__fdCheckoutGuardVersion) return;
  window.__fdCheckoutGuardVersion = 'checkout-entry-guard-v4-20260922';
  const BCPO_ADD_ON_ENDPOINT = 'https://obscure-escarpment-2240.herokuapp.com/add-ons';
  const CHECKOUT_TIMEOUT_MS = 8000;
  // Checkout creation is a separate, non-idempotent service request. Allow it
  // more time than a cart read, but never retry an unknown creation result.
  const ADD_ON_REQUEST_TIMEOUT_MS = 30000;
  const CONFIRMED_CHECKOUT_RETRY_TEXT = 'Your secure checkout is ready, but we could not recheck your cart. Please select Checkout again to continue. Your cart is saved.';
  const CURRENCY_CONTEXT_ERROR_TEXT = 'Your cart currency has changed. Please refresh this page and review your custom options before checking out. Your cart is saved.';
  const UNCERTAIN_CHECKOUT_ERROR_TEXT = 'We could not confirm the result of your checkout request. Your cart is saved. Please contact us before trying again so we can help you avoid a duplicate checkout.';
  const CHECKOUT_ERROR_TEXT = "We couldn't confirm your custom-option pricing. Your cart is saved. Please try Checkout again or contact us for help.";
  const CURRENCY_CHANGE_ERROR_TEXT = 'We could not confirm that every custom option is priced in {currency}. Please remove the affected custom item and configure it again in this currency, or contact us for help. Your other items are saved.';
  const TRUSTED_CHECKOUT_HOSTS = Object.freeze([
    'fredesigner.com',
    'www.fredesigner.com',
    'c5e1ec-d7.myshopify.com'
  ]);
  const GUARDED_NEGATIVE_COLOR_PRODUCT_IDS = new Set([
    '9267957629178',
    '9267981189370',
    '9267998163194'
  ]);
  const COLOR_PRICE_GUARD_VERSION = 'negative-color-v2';
  const PRIVATE_PRICE_KEYS = {
    customization: ['_fd_customization'],
    base: ['_fd_bcpo_base_cents', '_fd_base_price_cents', '_fd_base_cents'],
    addOn: [
      '_fd_bcpo_addon_cents',
      '_fd_bcpo_add_on_cents',
      '_fd_addon_price_cents',
      '_fd_add_on_price_cents',
      '_fd_addon_cents'
    ],
    configured: [
      '_fd_bcpo_configured_cents',
      '_fd_configured_price_cents',
      '_fd_configured_unit_cents',
      '_fd_configured_cents'
    ],
    currency: ['_fd_currency'],
    checksum: ['_fd_selection_checksum'],
    state: ['_fd_price_state'],
    guardVersion: ['_fd_price_guard_version'],
    colorAdjustment: ['_fd_color_adjustment_cents']
  };

  let activeConfiguredCart = null;
  let hasAppliedInitialCart = false;
  let checkoutFlight = null;
  let addOnRequestFlight = null;
  let checkoutNavigating = false;
  let checkoutResultUncertain = false;

  const getActiveCurrency = () => String(
    window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'USD'
  ).toUpperCase();

  const currencyTokenMatches = (token, currency, verifiedCurrency = '') => {
    const normalizedToken = String(token || '').toUpperCase();
    const normalizedCurrency = String(currency || '').toUpperCase();
    // Unmarked dollar prices use the historical USD contract. Other dollar
    // currencies need the complete, validated private pricing record.
    if (normalizedToken === '$') return normalizedCurrency === 'USD'
      || (['CAD', 'AUD'].includes(normalizedCurrency) && verifiedCurrency === normalizedCurrency);
    if (normalizedCurrency === 'USD') return ['US$', 'USD'].includes(normalizedToken);
    if (normalizedCurrency === 'CAD') return ['CA$', 'C$', 'CAD'].includes(normalizedToken);
    if (normalizedCurrency === 'AUD') return ['AU$', 'A$', 'AUD'].includes(normalizedToken);
    if (normalizedCurrency === 'EUR') return ['€', 'EUR'].includes(normalizedToken);
    if (normalizedCurrency === 'GBP') return ['£', 'GBP'].includes(normalizedToken);
    return normalizedToken === normalizedCurrency;
  };

  const readDisplayedAddOn = (value) => {
    const text = String(value == null ? '' : value);
    const separator = text.lastIndexOf('|');
    if (separator < 0) return { matched: false, isValid: true };
    const priceText = text.slice(separator + 1).trim();
    if (!/[$€£]|\b(?:USD|CAD|EUR|GBP|AUD)\b/i.test(priceText)) {
      return { matched: false, isValid: true };
    }
    // Only a complete en-US amount is accepted. Do not guess decimal/grouping
    // separators or accept a valid numeric prefix followed by extra content.
    const match = priceText.match(/^([+-]?)\s*((?:US|CA|AU|A|C)?\$|€|£|USD|CAD|EUR|GBP|AUD)\s*([+-]?)\s*((?:\d+|[1-9]\d{0,2}(?:,\d{3})+)(?:\.\d{1,2})?)$/i);
    if (!match || (match[1] && match[3])) return { matched: true, isValid: false };
    const parts = match[4].replace(/,/g, '').split('.');
    const magnitude = Number(parts[0]) * 100 + Number((parts[1] || '').padEnd(2, '0'));
    const cents = (match[1] || match[3]) === '-' ? -magnitude : magnitude;
    if (!Number.isSafeInteger(cents)) return { matched: true, isValid: false };
    return { matched: true, isValid: true, token: match[2], cents, separator };
  };

  const parsePrivateCents = (value) => {
    const text = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(text)) return null;
    const cents = Number(text);
    return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
  };

  const parseSignedCents = (value) => {
    const text = String(value == null ? '' : value).trim();
    if (!/^-?\d+$/.test(text)) return null;
    const cents = Number(text);
    return Number.isSafeInteger(cents) ? cents : null;
  };

  const canonicalPropertyValue = (value) => {
    const text = String(value == null ? '' : value);
    const displayed = readDisplayedAddOn(text);
    return (displayed.matched && displayed.isValid ? text.slice(0, displayed.separator) : text).trim();
  };

  const selectionPairsFromProperties = (properties) => Object.entries(properties || {})
    .filter(([key]) => {
      const name = String(key || '').trim();
      return name && !name.startsWith('_');
    })
    .map(([key, value]) => {
      const name = String(key || '').trim();
      const canonicalValue = canonicalPropertyValue(value);
      return canonicalValue ? `${name}=${canonicalValue}` : '';
    })
    .filter(Boolean)
    .filter((pair, index, pairs) => pairs.indexOf(pair) === index)
    .sort();

  const selectionChecksum = (pairs) => {
    const text = pairs.join('\n');
    let hash = 2166136261;
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
  };

  const readPrivatePricing = (properties, currency) => {
    const entries = Object.entries(properties || {});
    const byName = new Map(entries.map(([key, value]) => [String(key).toLowerCase(), value]));
    const read = (names, parser = parsePrivateCents) => {
      const key = names.find((name) => byName.has(name));
      if (!key) return { present: false, value: null };
      return { present: true, value: parser(byName.get(key)) };
    };
    const base = read(PRIVATE_PRICE_KEYS.base);
    const addOn = read(PRIVATE_PRICE_KEYS.addOn, parseSignedCents);
    const configured = read(PRIVATE_PRICE_KEYS.configured);
    const readText = (names) => {
      const key = names.find((name) => byName.has(name));
      if (!key) return { present: false, value: '' };
      return { present: true, value: String(byName.get(key) == null ? '' : byName.get(key)).trim() };
    };
    const customization = readText(PRIVATE_PRICE_KEYS.customization);
    const state = readText(PRIVATE_PRICE_KEYS.state);
    const propertyCurrency = readText(PRIVATE_PRICE_KEYS.currency);
    const checksum = readText(PRIVATE_PRICE_KEYS.checksum);
    const guardVersion = readText(PRIVATE_PRICE_KEYS.guardVersion);
    const colorAdjustment = read(PRIVATE_PRICE_KEYS.colorAdjustment, parseSignedCents);
    const hasAny = customization.present
      || base.present
      || addOn.present
      || configured.present
      || state.present
      || propertyCurrency.present
      || checksum.present
      || guardVersion.present
      || colorAdjustment.present;
    const amountFieldsComplete = base.present
      && addOn.present
      && configured.present
      && base.value !== null
      && addOn.value !== null
      && configured.value !== null;
    const selectionPairs = selectionPairsFromProperties(properties);
    const calculatedChecksum = selectionChecksum(selectionPairs);
    const currencyMatches = propertyCurrency.present
      && propertyCurrency.value.toUpperCase() === String(currency || '').toUpperCase();
    const markersComplete = customization.value === '1'
      && state.value.toLowerCase() === 'ready'
      && currencyMatches
      && checksum.value === calculatedChecksum;
    return {
      hasAny,
      isValid: !hasAny || (amountFieldsComplete && markersComplete),
      base,
      addOn,
      configured,
      customization,
      state,
      currency: propertyCurrency,
      currencyMatches,
      checksum,
      guardVersion,
      colorAdjustment,
      calculatedChecksum,
      selectionPairs
    };
  };

  const readLegacyPricing = (properties, currency, verifiedCurrency = '') => {
    let matched = false;
    let isValid = true;
    let addOnCents = 0;
    let nonZeroMatchCount = 0;
    let currencyMismatch = false;
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (String(key).startsWith('_')) return;
      const displayed = readDisplayedAddOn(value);
      if (!displayed.matched) return;
      matched = true;
      if (!displayed.isValid) {
        isValid = false;
        return;
      }
      if (!currencyTokenMatches(displayed.token, currency, verifiedCurrency)) {
        currencyMismatch = true;
        isValid = false;
        return;
      }
      addOnCents += displayed.cents;
      if (!Number.isSafeInteger(addOnCents)) isValid = false;
      if (displayed.cents !== 0) nonZeroMatchCount += 1;
    });
    return { matched, isValid, addOnCents, nonZeroMatchCount, currencyMismatch };
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const readServerCartEvidence = (root = document) => {
    const byLineKey = new Map();
    root.querySelectorAll('script[data-fd-cart-server-evidence]').forEach((node) => {
      let rows;
      try {
        rows = JSON.parse(node.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!Array.isArray(rows)) return;
      rows.forEach((row) => {
        const lineKey = String(row && row.lineKey || '').trim();
        if (!lineKey
          || typeof row.customizationRequired !== 'boolean'
          || typeof row.configuredPriceStateRequired !== 'boolean'
          || typeof row.bcpoDataPresent !== 'boolean') return;
        byLineKey.set(lineKey, {
          customizationRequired: row.customizationRequired,
          configuredPriceStateRequired: row.configuredPriceStateRequired,
          bcpoDataPresent: row.bcpoDataPresent,
          productId: String(row.productId || ''),
          variantId: String(row.variantId || '')
        });
      });
    });
    return byLineKey;
  };

  const calculateConfiguredCart = (cart, serverEvidenceByLineKey = readServerCartEvidence()) => {
    const currency = String(cart && cart.currency || getActiveCurrency()).toUpperCase();
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    let addOnSubtotalCents = 0;
    let displaySubtotalCents = 0;
    let customizedItemCount = 0;
    let nonZeroAddOnLineCount = 0;
    let hasCurrencyMismatch = false;
    let isValid = true;
    const validationErrors = [];
    const lines = new Map();

    items.forEach((item, index) => {
      const quantity = Number(item.quantity);
      const lineKey = String(item && item.key || '');
      const serverEvidence = serverEvidenceByLineKey.get(lineKey);
      const hasServerEvidence = Boolean(serverEvidence);
      const serverIdentityMatches = Boolean(
        serverEvidence
        && serverEvidence.productId
        && serverEvidence.variantId
        && serverEvidence.productId === String(item && item.product_id || '')
        && serverEvidence.variantId === String(item && item.variant_id || '')
      );
      const serverRequiresCustomization = Boolean(
        serverEvidence && serverEvidence.customizationRequired
      );
      const serverRequiresConfiguredPriceState = Boolean(
        serverEvidence && serverEvidence.configuredPriceStateRequired
      );
      const serverBcpoDataPresent = Boolean(serverEvidence && serverEvidence.bcpoDataPresent);
      const privatePricing = readPrivatePricing(item && item.properties, currency);
      const verifiedCurrency = privatePricing.hasAny && privatePricing.isValid
        ? privatePricing.currency.value.toUpperCase() : '';
      const legacyPricing = readLegacyPricing(item && item.properties, currency, verifiedCurrency);
      const usesPrivateAddOn = privatePricing.hasAny;
      const lineHasCurrencyMismatch = (usesPrivateAddOn
        && privatePricing.currency.present
        && !privatePricing.currencyMatches) || legacyPricing.currencyMismatch;
      if (lineHasCurrencyMismatch) hasCurrencyMismatch = true;
      const guardedColorPriceRequired = Boolean(
        serverEvidence
        && GUARDED_NEGATIVE_COLOR_PRODUCT_IDS.has(serverEvidence.productId)
      );
      const hasColorSelection = Object.entries(item && item.properties || {}).some(([key, value]) => (
        String(key || '').trim().toLowerCase() === 'color'
        && Boolean(canonicalPropertyValue(value))
      ));
      const guardedColorPriceIsValid = !guardedColorPriceRequired || (
        usesPrivateAddOn
        && privatePricing.guardVersion.present
        && privatePricing.guardVersion.value === COLOR_PRICE_GUARD_VERSION
        && privatePricing.colorAdjustment.present
        && Number.isSafeInteger(privatePricing.colorAdjustment.value)
        && hasColorSelection
      );
      const hasBcpoPricingEvidence = privatePricing.hasAny || legacyPricing.matched;
      const isCustomized = serverRequiresCustomization
        || (serverBcpoDataPresent && hasBcpoPricingEvidence);
      const authoritativeBaseUnitCents = [
        item && item.original_price,
        item && item.price,
        item && item.final_price
      ]
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map(Number)
        .find((value) => Number.isSafeInteger(value) && value >= 0);
      const baseUnitCents = usesPrivateAddOn
        ? privatePricing.base.value
        : Number(item.final_price);
      const addOnUnitCents = usesPrivateAddOn
        ? privatePricing.addOn.value
        : legacyPricing.addOnCents;
      const configuredUnitCents = usesPrivateAddOn
        ? privatePricing.configured.value
        : baseUnitCents + addOnUnitCents;
      const baseLineCents = Number(item.final_line_price);
      const privateAddOnTransportIsValid = !usesPrivateAddOn
        || (addOnUnitCents === 0 && !legacyPricing.matched)
        || (
          legacyPricing.matched
          && legacyPricing.isValid
          && legacyPricing.addOnCents === addOnUnitCents
        );
      const lineIsValid = Number.isSafeInteger(quantity)
        && quantity >= 0
        && hasServerEvidence
        && serverIdentityMatches
        && (!serverRequiresCustomization || serverBcpoDataPresent)
        && (!serverRequiresCustomization || hasBcpoPricingEvidence)
        && (!serverRequiresConfiguredPriceState || usesPrivateAddOn)
        && (!hasBcpoPricingEvidence || serverBcpoDataPresent)
        && privatePricing.isValid
        && guardedColorPriceIsValid
        && (usesPrivateAddOn ? privatePricing.isValid : legacyPricing.isValid)
        && privateAddOnTransportIsValid
        && Number.isSafeInteger(baseUnitCents)
        && baseUnitCents >= 0
        && (!usesPrivateAddOn || baseUnitCents === authoritativeBaseUnitCents)
        && Number.isSafeInteger(addOnUnitCents)
        && Number.isSafeInteger(configuredUnitCents)
        && configuredUnitCents >= 0
        && baseUnitCents + addOnUnitCents === configuredUnitCents
        && Number.isSafeInteger(baseLineCents)
        && baseLineCents >= 0
        && Number.isSafeInteger(addOnUnitCents * quantity)
        && Number.isSafeInteger(baseLineCents + (addOnUnitCents * quantity))
        && baseLineCents + (addOnUnitCents * quantity) >= 0;

      if (isCustomized) customizedItemCount += 1;
      if (!lineIsValid) {
        isValid = false;
        validationErrors.push(lineHasCurrencyMismatch
          ? `currency-line-${index + 1}`
          : `line-${index + 1}`);
      }

      const safeQuantity = Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : 0;
      const safeAddOnUnitCents = Number.isSafeInteger(addOnUnitCents)
        ? addOnUnitCents
        : 0;
      const safeBaseLineCents = Number.isSafeInteger(baseLineCents) && baseLineCents >= 0
        ? baseLineCents
        : 0;
      const addOnLineCents = safeAddOnUnitCents * safeQuantity;
      if (
        safeQuantity > 0
        && (
          safeAddOnUnitCents !== 0
          || (legacyPricing.isValid && legacyPricing.nonZeroMatchCount > 0)
        )
      ) {
        nonZeroAddOnLineCount += 1;
      }
      addOnSubtotalCents += addOnLineCents;
      // C96 (review Claude-95 P2-4): what the line shows: the price before discounts plus the add-ons,
      // as checkout lists it (the discounts come off at checkout). Display only; the guard keeps configuredLineCents.
      const originalLineCents = Number(item && item.original_line_price);
      const displayLineCents = Number.isSafeInteger(originalLineCents) && originalLineCents >= 0
        ? originalLineCents + addOnLineCents
        : safeBaseLineCents + addOnLineCents;
      displaySubtotalCents += displayLineCents;
      lines.set(lineKey, {
        configuredLineCents: safeBaseLineCents + addOnLineCents,
        displayLineCents,
        addOnLineCents,
        addOnUnitCents: safeAddOnUnitCents,
        isCustomized,
        pricingSource: usesPrivateAddOn ? 'private' : legacyPricing.matched ? 'legacy' : 'none',
        lineHasCurrencyMismatch,
        sourceCurrency: privatePricing.currency.value,
        serverRequiresCustomization,
        serverRequiresConfiguredPriceState,
        serverBcpoDataPresent,
        guardedColorPriceRequired,
        guardedColorPriceIsValid
      });
    });

    const baseSubtotalCents = Number(cart && cart.total_price);
    if (!Number.isSafeInteger(addOnSubtotalCents)
      || !Number.isSafeInteger(baseSubtotalCents + addOnSubtotalCents)
      || baseSubtotalCents + addOnSubtotalCents < 0) {
      isValid = false;
      validationErrors.push('configured-cart-total');
    }
    if (!Number.isSafeInteger(baseSubtotalCents) || baseSubtotalCents < 0) {
      isValid = false;
      validationErrors.push('cart-total');
    }

    return {
      currency,
      lines,
      hasPriceAddOnProperties: customizedItemCount > 0,
      customizedItemCount,
      nonZeroAddOnLineCount,
      hasCurrencyMismatch,
      isValid,
      validationErrors,
      addOnSubtotalCents,
      // C96 (P2-4): "Subtotal before discounts" = checkout's Subtotal (the sum of the shown lines).
      displaySubtotalCents: Number.isSafeInteger(displaySubtotalCents) && displaySubtotalCents >= 0
        ? displaySubtotalCents : null,
      configuredSubtotalCents: (
        Number.isSafeInteger(baseSubtotalCents) && baseSubtotalCents >= 0 ? baseSubtotalCents : 0
      ) + addOnSubtotalCents
    };
  };

  // C96 (P2-4): the cart page and the mini cart show one figure, before discounts, labelled so.
  const SUBTOTAL_LABEL = 'Subtotal before discounts';
  const shownLineCents = line => (Number.isSafeInteger(line.displayLineCents) ? line.displayLineCents : line.configuredLineCents);
  const shownSubtotalCents = configured => (Number.isSafeInteger(configured.displaySubtotalCents)
    ? configured.displaySubtotalCents : configured.configuredSubtotalCents);

  const updateCartPage = (cart, configured) => {
    const page = document.querySelector('[data-fd-cart-page]');
    if (!page) return;

    const byKey = new Map((cart.items || []).map((item) => [String(item.key || ''), item]));
    page.querySelectorAll('[data-fd-cart-item]').forEach((node) => {
      const key = String(node.dataset.fdCartKey || '');
      const item = byKey.get(key);
      const line = configured.lines.get(key);
      if (!item || !line) return;

      node.dataset.fdCartBaseUnitCents = String(Number(item.final_price) || 0);
      const linePrice = node.querySelector('[data-fd-cart-line-price]');
      if (linePrice) {
        linePrice.textContent = formatMoney(shownLineCents(line), configured.currency);
        linePrice.dataset.fdConfiguredLineCents = String(line.configuredLineCents);
      }
    });

    const subtotal = page.querySelector('[data-fd-cart-subtotal]');
    if (subtotal) {
      subtotal.textContent = formatMoney(shownSubtotalCents(configured), configured.currency);
      subtotal.dataset.fdConfiguredSubtotalCents = String(configured.configuredSubtotalCents);
    }
  };

  const updateMiniCart = (configured) => {
    document.querySelectorAll('[data-fd-cart-panel-item]').forEach((node) => {
      const line = configured.lines.get(String(node.dataset.fdCartPanelKey || ''));
      if (!line) return;
      const priceNodes = Array.from(node.querySelectorAll('strong'));
      const price = priceNodes[priceNodes.length - 1];
      if (price) {
        price.textContent = formatMoney(shownLineCents(line), configured.currency);
        price.dataset.fdConfiguredLineCents = String(line.configuredLineCents);
      }
    });

    document.querySelectorAll('[data-fd-mini-cart-subtotal]').forEach((subtotal) => {
      subtotal.textContent = formatMoney(shownSubtotalCents(configured), configured.currency);
      subtotal.dataset.fdConfiguredSubtotalCents = String(configured.configuredSubtotalCents);
      const label = subtotal.parentElement && subtotal.parentElement.querySelector('span');
      if (label) label.textContent = SUBTOTAL_LABEL;
    });

    document.querySelectorAll('.fd-cart-panel__assurance').forEach((list) => {
      const items = list.querySelectorAll('li');
      // Claude-72: Black Friday USD 225 line while window.fdBlackFriday (footer) says so; otherwise unchanged.
      // C96 (review Claude-95 P3-11): the shipping rates are said once, in the note under the buttons.
      if (items[1]) items[1].textContent = 'Ships to the U.S., Canada and five European countries';
      if (items[2]) items[2].textContent = 'Estimated U.S./Canada delivery: 7-10 business days after order confirmation';
    });
    document.querySelectorAll('.fd-cart-panel__notice').forEach((notice) => {
      notice.textContent = 'Subtotal before discounts includes selected add-ons; discounts are applied at checkout. Review the current Shipping Policy. ' + ((window.fdBlackFriday && window.fdBlackFriday.ship225()) ? 'Black Friday through Nov 30, standard shipping after discounts: Europe USD 23 up to USD 200, USD 17.99 from USD 200.01 to USD 224.99; U.S./Canada USD 18 below USD 225. Free standard shipping from USD 225 to all listed destinations.' : 'Standard shipping after discounts: Europe USD 23 up to USD 200, USD 17.99 from USD 200.01 to USD 299.99; U.S./Canada USD 18 below USD 300. Free standard shipping from USD 300 to all listed destinations.');
    });
  };

  // B08 local candidate; enabled only by the reviewed cart section marker (default off).
  const b08PriceStateEnabled = () => Boolean(document.querySelector('[data-fd-cart-page][data-fd-price-state-guard]'));
  let b08PricePhase = 'loading';
  let b08PriceGeneration = 0;
  let b08PriceFlight = null;
  let b08PriceRefreshQueued = false;
  let b08PriceUIInstalled = false;
  let b08InitialPriceRead = false;
  const b08SyncCheckout = () => {
    const page = document.querySelector('[data-fd-cart-page][data-fd-price-state-guard]');
    if (!page) return;
    const coordinator = window.__fdCartCoordinator;
    const ready = b08PricePhase === 'ready' && activeConfiguredCart?.isValid === true
      && coordinator && !coordinator.pendingCount() && !checkoutFlight && !checkoutNavigating && !b08PriceFlight;
    page.querySelectorAll('[name="checkout"]').forEach(control => {
      if (control.disabled !== !ready) control.disabled = !ready;
      if (ready) control.removeAttribute('data-fd-checkout-guarded');
      else control.setAttribute('data-fd-checkout-guarded', '');
    });
    page.querySelectorAll('[data-fd-price-refresh]').forEach(control => {
      control.hidden = b08PricePhase === 'ready';
      if (control.disabled !== Boolean(b08PriceFlight)) control.disabled = Boolean(b08PriceFlight);
    });
  };
  const b08SetPricePhase = phase => {
    const page = document.querySelector('[data-fd-cart-page][data-fd-price-state-guard]');
    if (!page) return;
    b08PricePhase = phase;
    page.dataset.fdPriceState = phase;
    const confirmed = phase === 'ready';
    const label = page.querySelector('[data-fd-price-label]');
    if (label) label.textContent = confirmed ? SUBTOTAL_LABEL : 'Subtotal';
    page.querySelectorAll('[data-fd-cart-price-confirmed-note]').forEach(node => {
      node.hidden = !confirmed;
      node.textContent = confirmed ? 'Subtotal before discounts includes option add-ons; discounts are applied at checkout. ' : '';
    });
    page.querySelectorAll('[data-fd-checkout-loading]').forEach(node => {
      node.hidden = confirmed;
      node.textContent = phase === 'invalid'
        ? "We couldn't confirm your custom price. Your cart is saved. Refresh the price or contact us for help."
        : 'Calculating your custom price…';
    });
    if (!confirmed) {
      document.documentElement.dataset.fdConfiguredCartValid = 'false';
      page.querySelectorAll('[data-fd-cart-line-price], [data-fd-cart-subtotal]').forEach(node => {
        node.textContent = phase === 'invalid' ? 'Price not confirmed' : 'Calculating…';
        delete node.dataset.fdConfiguredLineCents;
        delete node.dataset.fdConfiguredSubtotalCents;
      });
    }
    b08SyncCheckout();
  };
  const b08InvalidatePrices = () => { b08PriceGeneration++; b08SetPricePhase('loading'); };
  const b08QuantitiesMatch = cart => {
    const page = document.querySelector('[data-fd-cart-page][data-fd-price-state-guard]');
    if (!page || !cart || !Array.isArray(cart.items)) return false;
    const nodes = Array.from(page.querySelectorAll('[data-fd-cart-item]'));
    if (nodes.length !== cart.items.length) return false;
    const byKey = new Map(cart.items.map(item => [String(item.key), item]));
    return nodes.every(node => {
      const item = byKey.get(String(node.dataset.fdCartKey));
      const input = node.querySelector('[data-fd-cart-input]');
      return item && input && String(input.value).trim() !== '' && Number(input.value) === item.quantity;
    });
  };
  const b08RefreshPrice = () => {
    if (!b08PriceStateEnabled()) return Promise.resolve(false);
    if (b08PriceFlight) { b08PriceRefreshQueued = true; return b08PriceFlight; }
    const generation = ++b08PriceGeneration;
    b08SetPricePhase('loading');
    const coordinator = window.__fdCartCoordinator;
    const operation = (async () => {
      if (!coordinator) throw new Error('B08 price coordinator missing');
      await coordinator.waitForIdle(CHECKOUT_TIMEOUT_MS);
      const revision = coordinator.revision;
      const [cart, evidence] = await Promise.all([getCart(), getFreshServerCartEvidence()]);
      if (generation !== b08PriceGeneration) return false;
      if (window.__fdCartCoordinator !== coordinator || coordinator.pendingCount() || coordinator.revision !== revision
        || !b08QuantitiesMatch(cart)) throw new Error('B08 price snapshot changed');
      applyCart(cart, evidence);
      return activeConfiguredCart?.isValid === true;
    })().catch(() => {
      if (generation === b08PriceGeneration) b08SetPricePhase('invalid');
      return false;
    });
    b08PriceFlight = operation.finally(() => {
      b08PriceFlight = null;
      b08SyncCheckout();
      if (b08PriceRefreshQueued) { b08PriceRefreshQueued = false; b08RefreshPrice(); }
    });
    b08SyncCheckout();
    return b08PriceFlight;
  };
  const b08InstallPriceUI = () => {
    if (b08PriceUIInstalled || !b08PriceStateEnabled()) return;
    b08PriceUIInstalled = true;
    const quantityLoading = event => {
      const control = event.target && event.target.closest && event.target.closest('[data-fd-cart-input], [data-fd-cart-action], [name="update"]');
      if (control && control.closest('[data-fd-cart-page][data-fd-price-state-guard]')) b08InvalidatePrices();
    };
    document.addEventListener('input', quantityLoading, true);
    document.addEventListener('change', quantityLoading, true);
    document.addEventListener('click', event => {
      const refresh = event.target && event.target.closest && event.target.closest('[data-fd-price-refresh]');
      if (refresh && refresh.closest('[data-fd-cart-page][data-fd-price-state-guard]')) {
        event.preventDefault(); event.stopImmediatePropagation(); b08RefreshPrice(); return;
      }
      quantityLoading(event);
    }, true);
    document.addEventListener('submit', event => {
      if (event.submitter && event.submitter.name === 'update') quantityLoading({ target: event.submitter });
    }, true);
    if (typeof MutationObserver === 'function') {
      const observer = new MutationObserver(b08SyncCheckout);
      observer.observe(document.querySelector('[data-fd-cart-page][data-fd-price-state-guard]'), { subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }
  };

  const applyCart = (cart, serverEvidenceByLineKey) => {
    if (!cart || !Array.isArray(cart.items)) return;
    hasAppliedInitialCart = true;
    const configured = calculateConfiguredCart(cart, serverEvidenceByLineKey);
    activeConfiguredCart = configured;
    if (b08PriceStateEnabled() && configured.isValid !== true) b08SetPricePhase('invalid');
    else { updateCartPage(cart, configured); if (b08PriceStateEnabled()) b08SetPricePhase('ready'); }
    updateMiniCart(configured);
    document.documentElement.dataset.fdConfiguredCartCurrency = configured.currency;
    document.documentElement.dataset.fdConfiguredCartAddOnCents = String(configured.addOnSubtotalCents);
    document.documentElement.dataset.fdConfiguredCartValid = String(configured.isValid);
    document.documentElement.dataset.fdConfiguredCartCurrencyMismatch = String(
      configured.hasCurrencyMismatch
    );
  };

  const cartJsonUrl = () => {
    const root = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart.js?fd_checkout=${Date.now()}`;
  };

  const cartHtmlUrl = () => {
    const root = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart?fd_checkout_evidence=${Date.now()}`;
  };

  const getCart = () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => {
      if (controller) controller.abort();
    }, CHECKOUT_TIMEOUT_MS);
    return fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Cart response was invalid.');
        return cart;
      })
      .finally(() => window.clearTimeout(timer));
  };

  const getFreshServerCartEvidence = () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => {
      if (controller) controller.abort();
    }, CHECKOUT_TIMEOUT_MS);
    return fetch(cartHtmlUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'text/html' },
      signal: controller ? controller.signal : undefined
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart evidence request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.querySelector('script[data-fd-cart-server-evidence]')) {
          throw new Error('Cart server evidence was missing.');
        }
        return readServerCartEvidence(parsed);
      })
      .finally(() => window.clearTimeout(timer));
  };

  const showCheckoutError = (message, context) => {
    const text = message || CHECKOUT_ERROR_TEXT;
    const containers = [
      document.querySelector('.fd-cart-summary'),
      document.querySelector('.fd-cart-panel__checkout'),
      context && context.control && context.control.parentElement
    ].filter(Boolean);
    Array.from(new Set(containers)).forEach((container) => {
      let error = container.querySelector('[data-fd-bcpo-checkout-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-fd-bcpo-checkout-error', '');
        error.setAttribute('role', 'alert');
        error.style.margin = '10px 0 0';
        error.style.padding = '10px 12px';
        error.style.border = '1px solid #a54b3d';
        error.style.borderRadius = '6px';
        error.style.background = '#fff4f1';
        error.style.color = '#7b2f24';
        error.style.fontSize = '12px';
        error.style.lineHeight = '1.4';
        container.appendChild(error);
      }
      error.textContent = text;
      error.hidden = false;
    });
    document.documentElement.dataset.fdBcpoCheckoutState = 'blocked';
    document.dispatchEvent(new CustomEvent('fd:bcpo-checkout-blocked', {
      detail: { reason: 'configured-price-not-confirmed' }
    }));
  };

  const clearCheckoutError = () => {
    document.querySelectorAll('[data-fd-bcpo-checkout-error]').forEach((error) => {
      error.hidden = true;
    });
  };

  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const waitForBcpo = () => new Promise((resolve, reject) => {
    const deadline = Date.now() + CHECKOUT_TIMEOUT_MS;
    const check = () => {
      const bcpoApi = window.bcpo;
      if (bcpoApi && bcpoApi.$ && typeof bcpoApi.$.ajax === 'function') {
        resolve(bcpoApi);
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error('BCPO checkout runtime was unavailable.'));
        return;
      }
      window.setTimeout(check, 100);
    };
    check();
  });

  const readCurrencyContext = (cart, expected) => {
    const page = window.Shopify && window.Shopify.currency || {};
    const currency = String(page.active || '').toUpperCase();
    const rate = Number(page.rate);
    if (!currency || currency !== String(cart.currency || '').toUpperCase()
      || !Number.isFinite(rate) || rate <= 0
      || (expected && (expected.currency !== currency || expected.rate !== rate))) {
      const error = new Error(CURRENCY_CONTEXT_ERROR_TEXT);
      error.currencyContextChanged = true;
      throw error;
    }
    return Object.freeze({ currency, rate });
  };

  const readCheckoutAttributes = (cart, bcpoApi) => {
    const attributes = {};
    const previous = bcpoApi.note_attributes || [];
    if (Array.isArray(previous)) previous.forEach((entry) => {
      if (entry && (entry.key || entry.name)) attributes[entry.key || entry.name] = entry.value;
    });
    else Object.assign(attributes, previous);
    Object.assign(attributes, bcpoApi.obj_attributes || {}, cart.attributes || {});
    document.querySelectorAll('[name^="attributes["]').forEach((control) => {
      if (control.disabled || (['checkbox', 'radio'].includes(control.type) && !control.checked)) return;
      const match = control.name.match(/^attributes\[([^\]]+)\]$/);
      if (match) attributes[match[1]] = control.value;
    });
    return Object.entries(attributes).map(([key, value]) => ({ key, value: String(value == null ? '' : value) }));
  };

  const b03CMergeScope = Object.freeze({"enabled":false,"captureEnabled":false,"shippingConflictReviewed":false,"expiresAtUtc":"2026-09-27T08:52:18.060Z","cartTokenSha256":null,"fieldsSignatureSha256":null,"cartSignaturesSha256":{"q1":null,"q2":null},"reason":"B03-J authorized single original q1 complete pre-hook binding capture 20260927"});
  const b03CSha256 = async value => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))), byte => byte.toString(16).padStart(2, '0')).join('');
// Local candidate only. This function does not create a checkout or change VO settings.
// The existing original-entry request is the sole request if a later experiment is approved.
async function supplierMergeExperiment(payload, context, scope) {
  const captureOnly = context.optIn === 'b03-c-20260927-capture';
  if (!captureOnly && context.optIn !== 'b03-c-20260927') return payload;
  const stop = (reason, envelopeCart) => {
    const error = new Error('B03-C merge experiment blocked: ' + reason);
    if (captureOnly && reason === 'cart envelope differs' && b03EDiagnosticWindow()) {
      b03HEnvelopeRecords.set(error, b03HEnvelopeFlags(envelopeCart));
      b03IRememberAttributeFailure(error, envelopeCart);
    }
    throw error;
  };
  const hashPattern = /^[0-9a-f]{64}$/;
  if (!scope || (captureOnly ? scope.captureEnabled !== true : scope.enabled !== true)) stop('the experiment is not enabled');
  if (!captureOnly && scope.shippingConflictReviewed !== true) stop('General price-based shipping warning has not been reviewed');
  const expiresAt = Date.parse(scope.expiresAtUtc || '');
  if (!Number.isFinite(expiresAt) || !Number.isFinite(context.now) || context.now >= expiresAt) stop('the approved experiment window expired or is missing');
  if (false // theme-id gate removed 10-27 (owner): any copy of this theme behaves like live
      || context.shop !== 'c5e1ec-d7.myshopify.com'
      || context.host !== 'www.fredesigner.com'
      || context.path !== '/cart') stop('store, draft, host or original cart page differs');
  if (!captureOnly && (!hashPattern.test(scope.cartTokenSha256 || '')
      || !hashPattern.test(scope.fieldsSignatureSha256 || '')
      || !hashPattern.test(scope.cartSignaturesSha256?.q1 || '')
      || !hashPattern.test(scope.cartSignaturesSha256?.q2 || ''))) stop('trusted original-cart bindings are missing');
  const cart = payload.cart;
  if (captureOnly && b03JCaptureEnabled && (!cart || typeof cart.token !== 'string' || !cart.token
      || cart.currency !== 'USD' || String(cart.note || '') !== '' || cart.items?.length !== 3)) stop('cart envelope differs', cart);
  const b03JRawBinding = captureOnly && b03JCaptureEnabled ? await b03JBindRaw(cart, context) : null;
  if (!cart || typeof cart.token !== 'string' || !cart.token
      || cart.currency !== 'USD' || String(cart.note || '') !== ''
      || (Object.keys(cart.attributes || {}).length && !b03JRawBinding) || cart.items?.length !== 3) stop('cart envelope differs', cart);
  const byVariant = new Map(cart.items.map(item => [String(item.variant_id), item]));
  if (byVariant.size !== 3) stop('duplicate or extra variant');
  const bolonia = byVariant.get('46946003124474');
  const jenna = byVariant.get('48949547958522');
  const quantity = bolonia?.quantity;
  if (quantity !== 1 && quantity !== 2) stop('Bolonia quantity differs');
  const seelId = quantity === 1 ? '49692762308858' : '49692762374394';
  const seel = byVariant.get(seelId);
  if (!bolonia || String(bolonia.product_id) !== '8680109310202'
      || bolonia.original_price !== 3900 || bolonia.requires_shipping !== true
      || !jenna || String(jenna.product_id) !== '9244243394810'
      || jenna.quantity !== 1 || jenna.original_price !== 249 || jenna.requires_shipping !== true
      || !seel || String(seel.product_id) !== '9309433037050'
      || seel.quantity !== 1 || seel.original_price !== (quantity === 1 ? 128 : 148)
      || seel.requires_shipping !== false || (seel.discount_allocations || []).length) stop('variant, quantity, price, shipping or Seel allocation differs');
  const configured = context.configured;
  const configuredLine = configured?.lines?.get(bolonia.key);
  if (configured?.isValid !== true || configured.customizedItemCount !== 1
      || configuredLine?.addOnUnitCents !== 21998
      || configured.addOnSubtotalCents !== quantity * 21998) stop('configured pricing evidence differs');
  const approvedSelections = [
    ['Color', '#HV2028-28 White'], ['Header', 'Pinch Pleat'], ['Width', '60'],
    ['Height', '84'], ['Liner', '100%-Blackout Lining 150GsM'],
    ['Memory Shaping', 'Width From 40 To 80 inches'], ['Tieback', 'No']
  ];
  const optionValues = new Set(Object.values(bolonia.properties || {})
    .filter(value => typeof value === 'string' || typeof value === 'number')
    .map(value => String(value).split('|')[0].trim()));
  if (!approvedSelections.every(([, value]) => optionValues.has(value))) stop('one of the seven approved product values differs');
  if (captureOnly && b03JRawBinding) return b03JCaptureReceipt(cart, context, b03JRawBinding, quantity, seel, approvedSelections);
  if (captureOnly) return {
    captureOnly: true,
    receipt: {
      kind: 'original-entry-visible-digest-capture', themeId: String(context.themeId),
      quantity, cartTokenSha256: await context.sha256(cart.token),
      cartSignatureSha256: await context.sha256(context.cartSignature),
      fieldsSignatureSha256: await context.sha256(context.fieldsSignature),
      lines: cart.items.map(item => ({ variantId: String(item.variant_id), productId: String(item.product_id),
        quantity: item.quantity, originalUnitCents: item.original_price, requiresShipping: item.requires_shipping })),
      optionAddOnUnitCents: configuredLine.addOnUnitCents,
      selections: approvedSelections.map(([label, value]) => ({ label, value })),
      selectionsMatchedByValueOnly: true,
      configuredGrossUnitCents: 25898,
      configuredDisplaySubtotalCents: configured.configuredSubtotalCents,
      checkoutCreationAllowed: false, trustedStorefrontCartEstablished: false
    }
  };
  // The existing cart signature includes every property, variant, quantity, note,
  // price, discount and selling plan. Exact approved q1/q2 receipts bind all seven
  // attributes plus private pricing fields without inventing their Ajax key shape.
  if (await context.sha256(cart.token) !== scope.cartTokenSha256
      || await context.sha256(context.fieldsSignature) !== scope.fieldsSignatureSha256
      || await context.sha256(context.cartSignature) !== scope.cartSignaturesSha256[`q${quantity}`]) stop('original cart or approved snapshot no longer matches');
  // Preserve the payload object and every supplier integration field. No price,
  // quantity, discount, selection, customer or shipping rate is synthesized here.
  payload.requires_shipping = true;
  return payload;
}

  const buildBcpoPayload = (cart, bcpoApi, currencyContext = readCurrencyContext(cart)) => {
    readCurrencyContext(cart, currencyContext);
    const settings = window.bcpo_settings || {};
    const tags = Array.isArray(bcpoApi.tags) ? bcpoApi.tags.join(', ') : '';
    const noteField = document.querySelector('[name="note"]');
    const moneyFormat = typeof bcpoApi.getMoneyFormat === 'function'
      ? bcpoApi.getMoneyFormat()
      : '${{amount}}';
    return {
      shop: window.Shopify && window.Shopify.shop,
      cart,
      afDiscountObject: window.afDiscountObject,
      discountOnCartProApp: window.discountOnCartProApp
        ? window.discountOnCartProApp.integration
        : null,
      disallowDiscountCodesInCheckout: settings.disallowDiscountCodesInCheckout,
      selections: window.bcpoSelections,
      variant: window.bcpoVariant,
      note: noteField ? noteField.value : String(cart.note || ''),
      note_attributes: readCheckoutAttributes(cart, bcpoApi),
      tags,
      currency: currencyContext.currency,
      rate: currencyContext.rate,
      shipping_address: bcpoApi.shipping_address,
      requires_shipping: settings.requires_shipping,
      money_format: moneyFormat,
      tax_price_addons: settings.tax_price_addons,
      langify: window.langify || window.TranslationLab ? 'on' : 'off',
      options_in_note: settings.options_in_note,
      shipping_title: settings.shipping_title,
      shipping_price: settings.shipping_price,
      properties_on_original: settings.properties_on_original,
      customer_id: window.__st && window.__st.cid
        ? `gid://shopify/Customer/${window.__st.cid}`
        : null
    };
  };

  const triggerBeforeCheckout = (bcpoApi, cart) => {
    if (typeof bcpoApi.$ !== 'function') return;
    const result = bcpoApi.$(document).triggerHandler('bcpo:beforeCheckout', [cart]);
    if (result) throw new Error('Checkout was cancelled by a BCPO integration.');
  };

  const callBcpoAddOnEndpoint = (payload, bcpoApi) => new Promise((resolve, reject) => {
    let request;
    try {
      request = bcpoApi.$.ajax({
        url: BCPO_ADD_ON_ENDPOINT,
        method: 'POST',
        type: 'POST',
        data: payload,
        dataType: 'text',
        timeout: ADD_ON_REQUEST_TIMEOUT_MS
      });
    } catch (error) {
      reject(error);
      return;
    }
    if (!request || typeof request.done !== 'function' || typeof request.fail !== 'function') {
      reject(new Error('BCPO checkout request could not be started.'));
      return;
    }
    request
      .done((response) => resolve(response))
      .fail((xhr, status, error) => {
        const failure = new Error(String(error || status || 'BCPO checkout request failed.'));
        const httpStatus = Number(xhr && xhr.status) || 0;
        const statusText = String(status || '').toLowerCase();
        failure.status = httpStatus;
        failure.retryable = httpStatus === 0
          || statusText === 'timeout'
          || httpStatus >= 500;
        reject(failure);
      });
  });

  const responseUrlValue = (response) => {
    if (typeof response === 'string') {
      const text = response.trim();
      if (!text) return '';
      try {
        const parsed = JSON.parse(text);
        if (parsed !== response) return responseUrlValue(parsed);
      } catch (error) {
        // Plain URL responses are expected from the current BCPO service.
      }
      return text;
    }
    if (response && typeof response === 'object') {
      return String(response.checkout_url || response.checkoutUrl || response.url || '').trim();
    }
    return '';
  };

  const isTrustedCheckoutHost = (hostname) => {
    const host = String(hostname || '').toLowerCase();
    return TRUSTED_CHECKOUT_HOSTS.includes(host);
  };

  const parsedResponseObject = (response) => {
    if (response && typeof response === 'object') return response;
    if (typeof response !== 'string') return null;
    const text = response.trim();
    if (!text || !/^[{\[]/.test(text)) return null;
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const validateResponsePricing = (response, configured) => {
    const object = parsedResponseObject(response);
    if (!object) return;
    const centsKeys = [
      'configuredSubtotalCents',
      'configured_subtotal_cents',
      'lineTotalCents',
      'line_total_cents',
      'amountCents',
      'amount_cents'
    ];
    const amountKey = centsKeys.find((key) => Object.prototype.hasOwnProperty.call(object, key));
    if (amountKey) {
      const amount = Number(object[amountKey]);
      if (!Number.isSafeInteger(amount)
        || amount !== configured.configuredSubtotalCents) {
        throw new Error('BCPO checkout returned a mismatched configured amount.');
      }
    }
    if (Object.prototype.hasOwnProperty.call(object, 'currency')
      && String(object.currency || '').toUpperCase() !== configured.currency) {
      throw new Error('BCPO checkout returned a mismatched currency.');
    }
  };

  const validateCheckoutUrl = (response, configured) => {
    validateResponsePricing(response, configured);
    const value = responseUrlValue(response);
    if (!value) throw new Error('BCPO checkout returned an empty URL.');
    let url;
    try {
      url = new URL(value, window.location.origin);
    } catch (error) {
      throw new Error('BCPO checkout returned an invalid URL.');
    }
    const path = url.pathname.replace(/\/+/g, '/');
    const isBareNativeCheckout = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(path);
    const hasTokenizedCheckoutPath = /\/checkouts?\/[^/]+/i.test(path)
      || /\/c\/pay\/[^/]+/i.test(path)
      || /\/cart\/c\/[^/]+/i.test(path);
    const hasDraftOrderInvoicePath = /^\/\d+\/invoices\/[A-Za-z0-9_-]+\/?$/.test(path);
    if (url.protocol !== 'https:'
      || url.username
      || url.password
      || url.port
      || !isTrustedCheckoutHost(url.hostname)
      || isBareNativeCheckout
      || (!hasTokenizedCheckoutPath && !hasDraftOrderInvoicePath)) {
      throw new Error('BCPO checkout returned an untrusted URL.');
    }
    return url.href;
  };

  const requestBcpoCheckout = (payload, bcpoApi, configured) => {
    if (addOnRequestFlight) return addOnRequestFlight;
    const operation = (async () => {
      document.documentElement.dataset.fdBcpoCheckoutAttempt = '1';
      // The service has no verified idempotency contract. An uncertain POST
      // must not be repeated automatically or by a second click on this page.
      try {
        const response = await callBcpoAddOnEndpoint(payload, bcpoApi);
        return validateCheckoutUrl(response, configured);
      } catch (error) {
        checkoutResultUncertain = true;
        throw error;
      }
    })();
    addOnRequestFlight = operation.finally(() => {
      addOnRequestFlight = null;
    });
    return addOnRequestFlight;
  };

  const setCheckoutBusy = () => {
    const snapshots = [];
    document.querySelectorAll('[name="checkout"], .fd-cart-panel__checkout, a[href]').forEach((control) => {
      const href = control.getAttribute && control.getAttribute('href');
      const isCheckoutControl = control.getAttribute && control.getAttribute('name') === 'checkout'
        || control.matches && control.matches('.fd-cart-panel__checkout')
        || href && isNativeCheckoutHref(href);
      if (!isCheckoutControl) return;
      snapshots.push({
        control,
        disabled: 'disabled' in control ? control.disabled : null,
        ariaBusy: control.getAttribute('aria-busy'),
        pointerEvents: control.style.pointerEvents,
        text: /^(BUTTON|A)$/i.test(control.tagName || '') ? control.textContent : null
      });
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.style.pointerEvents = 'none';
      if (/^(BUTTON|A)$/i.test(control.tagName || '')) {
        control.textContent = 'Confirming your custom price…';
      }
    });
    return () => snapshots.forEach((snapshot) => {
      if (!snapshot.control || !snapshot.control.isConnected) return;
      if (snapshot.disabled !== null) snapshot.control.disabled = snapshot.disabled;
      if (snapshot.ariaBusy === null) snapshot.control.removeAttribute('aria-busy');
      else snapshot.control.setAttribute('aria-busy', snapshot.ariaBusy);
      snapshot.control.style.pointerEvents = snapshot.pointerEvents;
      if (snapshot.text !== null) snapshot.control.textContent = snapshot.text;
    });
  };

  const nativeCheckoutPath = (pathname) => /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(
    String(pathname || '').replace(/\/+/g, '/')
  );

  function isNativeCheckoutHref(href) {
    try {
      const url = new URL(String(href || ''), window.location.origin);
      return url.origin === window.location.origin && nativeCheckoutPath(url.pathname);
    } catch (error) {
      return false;
    }
  }

  const continueNativeCheckout = (context) => {
    const form = context && context.form;
    if (form) {
      // Native submit avoids replaying the BCPO click handler. Validate first;
      // the caller has restored disabled controls before serialization.
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        throw new Error('Please complete the required cart fields.');
      }
      let checkoutInput;
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        form.appendChild(checkoutInput);
      }
      try {
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        if (checkoutInput) checkoutInput.remove();
      }
      return;
    }

    const href = context && context.control && context.control.getAttribute('href');
    if (!isNativeCheckoutHref(href)) throw new Error('Native checkout destination was invalid.');
    window.location.assign(new URL(href, window.location.origin).href);
  };

  const isCartForm = (form) => {
    if (!form || !form.matches || !form.matches('form')) return false;
    try {
      const action = new URL(form.action || '/cart', window.location.origin);
      return action.origin === window.location.origin
        && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(action.pathname);
    } catch (error) { return false; }
  };

  const getCheckoutClickContext = (event) => {
    const target = event.target && event.target.closest
      ? event.target.closest('[name="checkout"], a[href], form.fd-cart-panel__checkout')
      : null;
    if (!target) return null;
    const name = target.getAttribute && target.getAttribute('name');
    if (name === 'checkout') {
      const form = target.form || target.closest('form');
      if (!isCartForm(form)) return null;
      return {
        control: target,
        form,
        forceConfigured: false
      };
    }
    const href = target.getAttribute && target.getAttribute('href');
    return isNativeCheckoutHref(href) ? { control: target, form: null } : null;
  };

  const getCheckoutSubmitContext = (event) => {
    const form = event.target;
    if (!isCartForm(form)) return null;
    const active = event.submitter;
    const activeName = active && active.getAttribute && active.getAttribute('name');
    const dedicatedCheckoutForm = form.matches('.fd-cart-panel__checkout');
    if (active && active.form === form && activeName !== 'checkout' && !dedicatedCheckoutForm) return null;
    // An implicit submit without an explicit checkout submitter can be an
    // update. Preserve the form's default behavior instead of guessing payment.
    if (activeName !== 'checkout' && !dedicatedCheckoutForm) return null;
    return { control: active || form, form };
  };

  const validateCheckoutControls = (context) => {
    const form = context && context.form;
    if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) {
      throw new Error('Please complete the required cart fields.');
    }
    const terms = document.querySelectorAll('#cart_agree, #agree, #CartTerms, #torden-terms-checkbox, .rterms-checkbox');
    for (const control of terms) {
      if (control.type === 'checkbox' && !control.disabled && control.getClientRects().length && !control.checked) {
        control.focus();
        throw new Error('Please accept the order terms before checking out.');
      }
    }
  };

  const canonical = (value) => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') return Object.keys(value).sort().reduce((out, key) => { out[key] = canonical(value[key]); return out; }, {});
    return value;
  };
  const cartSignature = (cart) => JSON.stringify(canonical({
    currency: cart.currency, note: cart.note || '', attributes: cart.attributes || {},
    total: cart.total_price, originalTotal: cart.original_total_price, discounts: cart.cart_level_discount_applications || [],
    items: cart.items.map(item => ({ key: item.key, id: item.id, variant: item.variant_id, product: item.product_id,
      quantity: item.quantity, properties: item.properties || {}, price: item.price, original: item.original_price,
      final: item.final_price, line: item.final_line_price, discounts: item.discount_allocations || [],
      sellingPlan: item.selling_plan_allocation || null }))
  }));
  const formSignature = () => JSON.stringify(Array.from(document.querySelectorAll('[name="note"], [name^="attributes["]')).map(c => [c.name,c.value,c.checked,c.disabled]));
  const uncertainStorageKey = 'fd-custom-checkout-pending-v2';
  const confirmedStorageKey = 'fd-custom-checkout-confirmed-v1';
  const CONFIRMED_CHECKOUT_TTL_MS = 30 * 60 * 1000;
  let pendingCheckoutInMemory = null;
  let confirmedCheckoutInMemory = null;
  const readPendingRecord = () => {
    let raw = pendingCheckoutInMemory;
    if (!raw) { try { raw = sessionStorage.getItem(uncertainStorageKey); } catch (_) { return null; } }
    if (!raw) return null;
    try {
      const record = JSON.parse(raw);
      if (record && typeof record.signature === 'string' && typeof record.supplierModeFingerprint === 'string'
        && typeof record.localAttemptId === 'string' && record.localAttemptId) return record;
      if (record && ('signature' in record || 'localAttemptId' in record || 'supplierModeFingerprint' in record)) return { invalid: true };
    } catch (_) {}
    // Legacy signature-only unknown creation cannot be linked to an old confirmed URL.
    return { signature: String(raw), supplierModeFingerprint: null, localAttemptId: null, legacy: true };
  };
  const readPendingCheckout = () => { const record = readPendingRecord(); return record && record.signature; };
  const savePendingCheckout = (signature, supplierModeFingerprint, localAttemptId) => {
    const record = JSON.stringify({ signature, supplierModeFingerprint, localAttemptId });
    pendingCheckoutInMemory = record;
    try { sessionStorage.setItem(uncertainStorageKey, record); } catch (_) {}
  };
  const clearPendingCheckout = (expectedAttemptId) => {
    const pending = readPendingRecord();
    if (pending && (!expectedAttemptId || pending.localAttemptId !== expectedAttemptId)) return false;
    pendingCheckoutInMemory = null;
    try { sessionStorage.removeItem(uncertainStorageKey); } catch (_) {}
    return true;
  };
  const readConfirmedCheckout = () => {
    if (confirmedCheckoutInMemory) return confirmedCheckoutInMemory;
    try { return JSON.parse(sessionStorage.getItem(confirmedStorageKey) || 'null'); } catch (_) { return null; }
  };
  const saveConfirmedCheckout = (record) => {
    confirmedCheckoutInMemory = record;
    try { sessionStorage.setItem(confirmedStorageKey, JSON.stringify(record)); } catch (_) {}
  };
  const checkoutCustomerId = () => String(window.__st && window.__st.cid || '');
  const recoverConfirmedCheckout = (cart, signature, fieldsSignature, currencyContext, configured, supplierModeFingerprint = 'ordinary') => {
    const saved = readConfirmedCheckout();
    const pending = readPendingRecord();
    if (pending && (!saved || pending.invalid || pending.legacy || pending.signature !== signature
      || pending.supplierModeFingerprint !== supplierModeFingerprint || !pending.localAttemptId
      || pending.localAttemptId !== saved.localAttemptId)) return '';
    if ((saved && saved.supplierModeFingerprint || 'ordinary') !== supplierModeFingerprint) return '';
    if (!saved || !Number.isFinite(saved.time) || Date.now() < saved.time
      || Date.now() - saved.time > CONFIRMED_CHECKOUT_TTL_MS
      || saved.signature !== signature || saved.fieldsSignature !== fieldsSignature
      || !cart.token || saved.cartToken !== String(cart.token)
      || saved.customerId !== checkoutCustomerId()
      || saved.currency !== currencyContext.currency || saved.rate !== currencyContext.rate
      || saved.configuredSubtotalCents !== configured.configuredSubtotalCents) return '';
    try { return validateCheckoutUrl(saved.url, configured); } catch (_) { return ''; }
  };

  // fd-bolonia-controlled-review-20261012-v1: unpublished fixed-configuration pilot.
  const BOLONIA_REVIEW_URL = 'https://fredesigner-rewards-test.onrender.com/customer/checkout/server-owned';
  const BOLONIA_REVIEW_PROPERTIES = Object.freeze({
    "Color": "#HV2028-28 White",
    "Hanging Header Style (Hooks included)": "Pinch Pleat | $50.00",
    "Measurement Finder What is Single Panel Width After Pleats Made? Please click here to know more Single Panel Width After Pleats Made (inch)": "60 | $20.00",
    "Single Panel Length (Height) in Inch": "84 | $34.00",
    "Liner selection affects final weight, click here to weight calculator Lining Type For Tailor Pleat": "100%-Blackout Lining 150GsM | $45.99",
    "Memory Shaping": "Width From 40 To 80 inches | $69.99",
    "Tieback": "No",
    "_fd_customization": "1",
    "_fd_base_price_cents": "3900",
    "_fd_addon_cents": "21998",
    "_fd_configured_unit_cents": "25898",
    "_fd_currency": "USD",
    "_fd_selection_checksum": "fnv1a-b56d657f",
    "_fd_price_state": "ready"
});
  const boloniaReviewSeelAttributes = (attributes) => {
    if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return false;
    const keys = Object.keys(attributes);
    return keys.length === 0 || keys.length === 3
      && keys.every(key => ['wfp-checked', 'wfp_quote_id', 'seel_user_id'].includes(key))
      && (attributes['wfp-checked'] === 'true' || attributes['wfp-checked'] === true)
      && typeof attributes.wfp_quote_id === 'string' && /^[1-9][0-9]{0,29}$/.test(attributes.wfp_quote_id)
      && typeof attributes.seel_user_id === 'string'
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(attributes.seel_user_id);
  };
  const boloniaReviewQuantity = (cart, configured) => {
    const shop = window.Shopify || {}, theme = shop.theme || {};
    if (shop.shop !== 'c5e1ec-d7.myshopify.com'
      || theme.role !== 'unpublished' || shop.country !== 'US'
      || shop.currency?.active !== 'USD' || Number(shop.currency?.rate) !== 1
      || cart?.currency !== 'USD' || !configured?.isValid
      || configured.customizedItemCount !== 1 || !Array.isArray(cart.items) || cart.items.length !== 2
      || typeof cart.token !== 'string' || !cart.token || String(cart.note || '').trim()
      || !boloniaReviewSeelAttributes(cart.attributes || {})) return 0;
    const item = cart.items.find(line => String(line.product_id) === '8680109310202'
      && String(line.variant_id) === '46946003124474');
    if (!item || ![1, 2].includes(item.quantity) || item.selling_plan_allocation) return 0;
    const expectedSeel = item.quantity === 1 ? '49692762308858' : '49692762374394';
    const seel = cart.items.find(line => String(line.product_id) === '9309433037050'
      && String(line.variant_id) === expectedSeel && line.quantity === 1);
    if (!seel || seel.selling_plan_allocation || Object.keys(seel.properties || {}).length) return 0;
    const actual = item.properties || {}, expected = BOLONIA_REVIEW_PROPERTIES;
    if (Object.keys(actual).length !== Object.keys(expected).length
      || Object.entries(expected).some(([key, value]) => actual[key] !== value)) return 0;
    const codes = cart.discount_codes || [];
    if (!Array.isArray(codes) || codes.some(code =>
      !code || String(code.code || '').trim().toUpperCase() !== 'NEW10')) return 0;
    if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) return 0;
    const fields = Array.from(document.querySelectorAll('[name="note"], [name^="attributes["], [name="discount"], [name="discount_code"]'));
    if (fields.some(field => !field.disabled
      && !(['checkbox', 'radio'].includes(field.type) && !field.checked)
      && String(field.value || '').trim()
      && !(['discount', 'discount_code'].includes(field.name)
        && String(field.value).trim().toUpperCase() === 'NEW10')
      && !Object.entries(cart.attributes || {}).some(([key, value]) =>
        field.name === 'attributes[' + key + ']' && field.value === String(value)))) return 0;
    return item.quantity;
  };
  const boloniaHasExistingVoCheckout = (cart) => {
    const saved = readConfirmedCheckout();
    return Boolean(checkoutResultUncertain || readPendingCheckout()
      || saved && (!saved.cartToken || saved.cartToken === String(cart.token)));
  };

  let leavingCartDocument = false;
  window.addEventListener('beforeunload', () => { leavingCartDocument = true; });
  window.addEventListener('pageshow', () => { leavingCartDocument = false; });
  const preparationStorageKey = 'fd-checkout-preparation-v1';
  const readPreparation = () => { try { return JSON.parse(sessionStorage.getItem(preparationStorageKey) || 'null'); } catch (_) { return null; } };
  const clearPreparation = () => { try { sessionStorage.removeItem(preparationStorageKey); } catch (_) {} };
  const savePreparation = (resumeCount) => { try {
    const fields = Array.from(document.querySelectorAll('[name="note"], [name^="attributes["]')).map(c => ({ name: c.name, value: c.value, checked: c.checked }));
    sessionStorage.setItem(preparationStorageKey, JSON.stringify({ time: Date.now(), resumeCount, path: location.pathname, fields }));
  } catch (_) {} };

  // B03-E local review only: visible diagnostics are gated to the approved capture window.
  const b03EDiagnosticRecords = new WeakMap();
  // B03-H local candidate: six booleans for the unchanged envelope assertion.
  const b03HEnvelopeRecords = new WeakMap();
  // B03-I local candidate only; a separate reviewed authorization is required.
  const b03IAttributeFingerprintEnabled = false;
  const b03IAttributeRecords = new WeakMap();
  const b03IAttributeSnapshot = attributes => {
    try {
      if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return null;
      const prototype = Object.getPrototypeOf(attributes);
      if (prototype !== Object.prototype && prototype !== null) return null;
      const keys = Reflect.ownKeys(attributes);
      if (keys.length > 128 || keys.some(key => typeof key !== 'string')) return null;
      keys.sort();
      const entries = [];
      let inputCharacters = 0;
      for (const key of keys) {
        const descriptor = Object.getOwnPropertyDescriptor(attributes, key);
        if (!descriptor.enumerable || !Object.prototype.hasOwnProperty.call(descriptor, 'value') || key.length > 256) return null;
        const value = descriptor.value;
        if (!(value === null || typeof value === 'string' || typeof value === 'boolean'
          || typeof value === 'number' && Number.isFinite(value))) return null;
        if (typeof value === 'string' && value.length > 4096) return null;
        inputCharacters += key.length + (typeof value === 'string' ? value.length : 32);
        if (inputCharacters > 8192) return null;
        entries.push([key, value]);
      }
      const canonical = JSON.stringify(['cart.attributes.sorted-entries.v1', entries]);
      return canonical.length <= 65536 ? canonical : null;
    } catch (_) { return null; }
  };
  const b03IRememberAttributeFailure = (error, cart) => {
    if (!b03IAttributeFingerprintEnabled || !b03EDiagnosticWindow()
      || !error || !b03HEnvelopeRecords.has(error)) return;
    const flags = b03HEnvelopeRecords.get(error);
    if (flags.cartPresent !== true || flags.tokenIsNonemptyString !== true
      || flags.currencyIsUSD !== true || flags.noteIsEmpty !== true
      || flags.itemCountIsThree !== true || flags.attributesAreEmpty !== false) return;
    try {
      const canonical = b03IAttributeSnapshot(cart.attributes);
      b03IAttributeRecords.set(error, typeof canonical === 'string' ? Object.freeze({
        canonical,
        entryCount: JSON.parse(canonical)[1].length,
        canonicalUtf8Bytes: new TextEncoder().encode(canonical).byteLength
      }) : null);
    } catch (_) { b03IAttributeRecords.set(error, null); }
  };
  const b03IReadAttributeDiagnostic = async error => {
    const diagnostic = b03ESafeDiagnostic(error);
    if (!b03IAttributeFingerprintEnabled || !b03EDiagnosticWindow()
      || diagnostic.stage !== 'scope' || diagnostic.code !== 'SCOPE_ENVELOPE'
      || !error || !b03IAttributeRecords.has(error)) return diagnostic;
    const snapshot = b03IAttributeRecords.get(error);
    const canonical = snapshot && snapshot.canonical;
    const result = { ...diagnostic, attributesFingerprintKind: 'cart.attributes.sorted-entries.v1',
      effectiveNoteAttributesBound: false };
    if (typeof canonical !== 'string') return { ...result, attributesFingerprintStatus: 'UNSUPPORTED' };
    result.attributesEntryCount = snapshot.entryCount;
    result.attributesCanonicalUtf8Bytes = snapshot.canonicalUtf8Bytes;
    let timer, digest = null;
    try {
      digest = await Promise.race([
        Promise.resolve().then(() => b03CSha256(canonical)).catch(() => null),
        new Promise(resolve => { timer = setTimeout(() => resolve(null), 1000); })
      ]);
    } catch (_) {} finally { clearTimeout(timer); }
    if (!b03IAttributeFingerprintEnabled || !b03EDiagnosticWindow()) return diagnostic;
    if (typeof digest !== 'string' || !/^[0-9a-f]{64}$/.test(digest)) {
      return { ...result, attributesFingerprintStatus: 'UNAVAILABLE' };
    }
    return { ...result, attributesFingerprintStatus: 'HASHED', attributesSha256: digest };
  };
  // B03-J local candidate: original-cart digest capture only; no creation authorization.
  const b03JCaptureEnabled = false;
  const b03JExpectedRawAttributes = Object.freeze({
    kind: 'cart.attributes.sorted-entries.v1', entryCount: 3, canonicalUtf8Bytes: 166,
    sha256: 'a5bb67f370c8c1f0d0db515e9abb3b865d2b9c9829966e4cc40cf62cc65caebc'
  });
  const b03JReceiptBindings = new WeakMap();
  const b03JArrayForEachDescriptor = Object.getOwnPropertyDescriptor(Array.prototype, 'forEach');
  const b03JBlock = status => { throw new Error('B03-J capture blocked: ' + status); };
  const b03JPendingAbsent = () => {
    let raw;
    try { raw = sessionStorage.getItem(uncertainStorageKey); }
    catch (_) { b03JBlock('PENDING_UNREADABLE'); }
    if (raw !== null || pendingCheckoutInMemory !== null) b03JBlock('UNKNOWN_PENDING');
  };
  const b03JPreflightCart = cart => {
    if (!b03EDiagnosticWindow()) b03JBlock('WINDOW');
    b03JPendingAbsent();
    const attributes = cart && cart.attributes;
    if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) b03JBlock('RAW_BINDING');
    const keys = Reflect.ownKeys(attributes);
    if (keys.length !== b03JExpectedRawAttributes.entryCount || keys.some(key => typeof key !== 'string')) b03JBlock('RAW_BINDING');
    if (typeof b03IAttributeSnapshot(attributes) !== 'string') b03JBlock('RAW_BINDING');
  };
  const b03JEntryContext = coordinator => {
    if (!b03EDiagnosticWindow()) b03JBlock('WINDOW');
    b03JPendingAbsent();
    const controls = document.querySelectorAll('[name="note"], [name^="attributes["]');
    if (controls.length > 128) b03JBlock('INPUT_UNSUPPORTED');
    let characters = 0;
    const fields = [];
    for (const control of controls) {
      if (typeof control.name !== 'string' || typeof control.value !== 'string'
        || control.name.length > 256 || control.value.length > 4096) b03JBlock('INPUT_UNSUPPORTED');
      characters += control.name.length + control.value.length;
      if (characters > 8192) b03JBlock('INPUT_UNSUPPORTED');
      fields.push([control.name, control.value, control.checked, control.disabled]);
    }
    return { coordinator, revision: coordinator.revision, fieldsSignature: JSON.stringify(fields) };
  };
  const b03JAssertEntry = entry => {
    if (!entry || window.__fdCartCoordinator !== entry.coordinator || entry.coordinator.pendingCount()
      || entry.coordinator.revision !== entry.revision
      || b03JEntryContext(entry.coordinator).fieldsSignature !== entry.fieldsSignature) b03JBlock('SOURCE_CHANGED');
  };
  const b03JFrame = (cart, context) => {
    if (!b03JCaptureEnabled || !b03EDiagnosticWindow()) b03JBlock('WINDOW');
    b03JPendingAbsent();
    b03JAssertEntry(context.b03JEntry);
    if (typeof context.assertCurrent !== 'function') b03JBlock('SOURCE_CHANGED');
    try { context.assertCurrent(); } catch (_) { b03JBlock('SOURCE_CHANGED'); }
    if (cartSignature(cart) !== context.cartSignature || formSignature() !== context.fieldsSignature) b03JBlock('SOURCE_CHANGED');
  };
  const b03JDigest = async (value, limit, sha256) => {
    if (typeof value !== 'string' || value.length > limit || new TextEncoder().encode(value).byteLength > limit) b03JBlock('INPUT_UNSUPPORTED');
    let timer;
    try {
      const digest = await Promise.race([
        Promise.resolve().then(() => sha256(value)).catch(() => null),
        new Promise(resolve => { timer = setTimeout(() => resolve(null), 1000); })
      ]);
      if (typeof digest !== 'string' || !/^[0-9a-f]{64}$/.test(digest)) b03JBlock('HASH_UNAVAILABLE');
      return digest;
    } finally { clearTimeout(timer); }
  };
  const b03JBindRaw = async (cart, context) => {
    b03JFrame(cart, context);
    const canonical = b03IAttributeSnapshot(cart.attributes);
    if (typeof canonical !== 'string') b03JBlock('RAW_BINDING');
    const entryCount = JSON.parse(canonical)[1].length;
    const canonicalUtf8Bytes = new TextEncoder().encode(canonical).byteLength;
    if (entryCount !== b03JExpectedRawAttributes.entryCount || canonicalUtf8Bytes !== b03JExpectedRawAttributes.canonicalUtf8Bytes) b03JBlock('RAW_BINDING');
    const sha256 = await b03JDigest(canonical, 65536, context.sha256);
    b03JFrame(cart, context);
    if (b03IAttributeSnapshot(cart.attributes) !== canonical || sha256 !== b03JExpectedRawAttributes.sha256) b03JBlock('RAW_BINDING');
    return Object.freeze({ canonical, receipt: Object.freeze({
      kind: b03JExpectedRawAttributes.kind, entryCount, canonicalUtf8Bytes, sha256, originalAttributesPreserved: true
    }) });
  };
  const b03JData = (object, key) => {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);
    if (!descriptor) {
      if (key in object) b03JBlock('INPUT_UNSUPPORTED');
      return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) b03JBlock('INPUT_UNSUPPORTED');
    return descriptor.value;
  };
  const b03JCacheCanonical = value => {
    if (value === undefined || value === null) return JSON.stringify([value === undefined ? 'missing' : 'null']);
    if (!Array.isArray(value)) {
      const canonical = b03IAttributeSnapshot(value);
      if (typeof canonical !== 'string' || canonical.length > 8192) b03JBlock('INPUT_UNSUPPORTED');
      return JSON.stringify(['bcpo.cache-object-data.v1', Reflect.ownKeys(value), canonical]);
    }
    const length = b03JData(value, 'length');
    if (!Number.isSafeInteger(length) || length < 0 || length > 128
      || Object.getPrototypeOf(value) !== Array.prototype) b03JBlock('INPUT_UNSUPPORTED');
    const forEach = Object.getOwnPropertyDescriptor(Array.prototype, 'forEach');
    if (!forEach || !Object.prototype.hasOwnProperty.call(forEach, 'value')
      || !b03JArrayForEachDescriptor || forEach.value !== b03JArrayForEachDescriptor.value
      || typeof forEach.value !== 'function'
      || Function.prototype.toString.call(forEach.value) !== 'function forEach() { [native code] }') b03JBlock('INPUT_UNSUPPORTED');
    const keys = Reflect.ownKeys(value);
    if (keys.length > 129
      || keys.some(key => typeof key !== 'string' || key !== 'length' && !/^(0|[1-9][0-9]*)$/.test(key))) b03JBlock('INPUT_UNSUPPORTED');
    const entries = [];
    let characters = 0;
    for (let index = 0; index < length; index++) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor) {
        if (String(index) in value) b03JBlock('INPUT_UNSUPPORTED');
        entries.push(['hole']); continue;
      }
      if (!descriptor.enumerable || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) b03JBlock('INPUT_UNSUPPORTED');
      const entry = descriptor.value;
      const canonical = entry === null ? 'null' : b03IAttributeSnapshot(entry);
      if (typeof canonical !== 'string') b03JBlock('INPUT_UNSUPPORTED');
      if (entry !== null) ['key', 'name', 'value'].forEach(key => b03JData(entry, key));
      characters += canonical.length;
      if (characters > 8192) b03JBlock('INPUT_UNSUPPORTED');
      entries.push(canonical);
    }
    return JSON.stringify(['bcpo.cache-array-data.v1', entries]);
  };
  const b03JSource = cart => {
    const api = b03JData(window, 'bcpo');
    if (!api || typeof api !== 'object') b03JBlock('INPUT_UNSUPPORTED');
    const previous = b03JData(api, 'note_attributes');
    const objectAttributes = b03JData(api, 'obj_attributes');
    if (objectAttributes != null && Array.isArray(objectAttributes)) b03JBlock('INPUT_UNSUPPORTED');
    const previousCanonical = b03JCacheCanonical(previous);
    const objectCanonical = b03JCacheCanonical(objectAttributes);
    const rawCanonical = b03IAttributeSnapshot(cart.attributes);
    if (typeof rawCanonical !== 'string') b03JBlock('INPUT_UNSUPPORTED');
    const controls = document.querySelectorAll('[name^="attributes["]');
    if (controls.length > 128) b03JBlock('INPUT_UNSUPPORTED');
    const effectiveControls = [];
    let characters = 0;
    for (const control of controls) {
      if (control.disabled || (['checkbox', 'radio'].includes(control.type) && !control.checked)) continue;
      if (typeof control.name !== 'string' || typeof control.value !== 'string') b03JBlock('INPUT_UNSUPPORTED');
      const match = control.name.match(/^attributes\[([^\]]+)\]$/);
      if (!match) continue;
      if (match[1].length > 256 || control.value.length > 4096) b03JBlock('INPUT_UNSUPPORTED');
      characters += match[1].length + control.value.length;
      if (characters > 8192) b03JBlock('INPUT_UNSUPPORTED');
      effectiveControls.push([match[1], control.value]);
    }
    const canonical = JSON.stringify([previousCanonical, objectCanonical, rawCanonical, Reflect.ownKeys(cart.attributes), effectiveControls]);
    if (canonical.length > 65536) b03JBlock('INPUT_UNSUPPORTED');
    return { api, canonical };
  };
  // Canonicalize the existing helper's actual array in its original order; do not sort or merge again.
  const b03JEffectiveCanonical = noteAttributes => {
    if (!Array.isArray(noteAttributes) || noteAttributes.length > 128) b03JBlock('INPUT_UNSUPPORTED');
    let characters = 0;
    const entries = [];
    for (let index = 0; index < noteAttributes.length; index++) {
      const descriptor = Object.getOwnPropertyDescriptor(noteAttributes, String(index));
      if (!descriptor || !descriptor.enumerable || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) b03JBlock('INPUT_UNSUPPORTED');
      const entry = descriptor.value;
      if (typeof b03IAttributeSnapshot(entry) !== 'string') b03JBlock('INPUT_UNSUPPORTED');
      const key = b03JData(entry, 'key'), value = b03JData(entry, 'value');
      if (typeof key !== 'string' || typeof value !== 'string' || key.length > 256 || value.length > 4096) b03JBlock('INPUT_UNSUPPORTED');
      characters += key.length + value.length;
      if (characters > 8192) b03JBlock('INPUT_UNSUPPORTED');
      entries.push({ key, value });
    }
    const canonical = JSON.stringify(['note_attributes.original-helper-output.v1', entries]);
    if (canonical.length > 65536) b03JBlock('INPUT_UNSUPPORTED');
    return canonical;
  };
  const b03JAssertBinding = binding => {
    b03JFrame(binding.cart, binding.context);
    if (b03IAttributeSnapshot(binding.cart.attributes) !== binding.raw.canonical) b03JBlock('SOURCE_CHANGED');
    const current = b03JSource(binding.cart);
    if (current.api !== binding.source.api || current.canonical !== binding.source.canonical) b03JBlock('SOURCE_CHANGED');
  };
  const b03JCaptureReceipt = async (cart, context, raw, quantity, seel, approvedSelections) => {
    b03JFrame(cart, context);
    const source = b03JSource(cart);
    const noteAttributes = readCheckoutAttributes(cart, source.api);
    const canonical = b03JEffectiveCanonical(noteAttributes);
    const binding = { cart, context, raw, source };
    b03JAssertBinding(binding);
    const [effectiveSha256, cartTokenSha256, cartSignatureSha256, fieldsSignatureSha256] = await Promise.all([
      b03JDigest(canonical, 65536, context.sha256), b03JDigest(cart.token, 4096, context.sha256),
      b03JDigest(context.cartSignature, 262144, context.sha256), b03JDigest(context.fieldsSignature, 65536, context.sha256)
    ]);
    b03JAssertBinding(binding);
    const lines = cart.items.map(item => {
      const pricing = context.configured.lines.get(item.key);
      const cents = [item.original_price, item.final_price, item.final_line_price, pricing?.configuredLineCents, pricing?.addOnLineCents];
      if (!cents.every(value => Number.isSafeInteger(value) && value >= 0)) b03JBlock('INPUT_UNSUPPORTED');
      return { variantId: String(item.variant_id), productId: String(item.product_id), quantity: item.quantity,
        originalUnitCents: item.original_price, finalBaseUnitCents: item.final_price, finalBaseLineCents: item.final_line_price,
        configuredLineCents: pricing.configuredLineCents, optionAddOnLineCents: pricing.addOnLineCents, requiresShipping: item.requires_shipping };
    });
    if (!Number.isSafeInteger(context.configured.configuredSubtotalCents)
      || lines.reduce((sum, line) => sum + line.configuredLineCents, 0) !== context.configured.configuredSubtotalCents) b03JBlock('INPUT_UNSUPPORTED');
    const receipt = {
      kind: 'B03-J-original-entry-complete-capture', themeId: String(context.themeId), quantity, quantityCase: 'q' + quantity,
      cartTokenSha256, cartSignatureSha256, fieldsSignatureSha256, rawCartAttributes: raw.receipt,
      effectiveNoteAttributes: { kind: 'note_attributes.original-helper-output.v1', phase: 'pre-hook', entryCount: noteAttributes.length,
        canonicalUtf8Bytes: new TextEncoder().encode(canonical).byteLength, sha256: effectiveSha256,
        actualPayloadBound: false, originalHelperArrayOrderPreserved: true, sourceStableAtSamples: true },
      lines, configuredDisplaySubtotalCents: context.configured.configuredSubtotalCents,
      optionAddOnUnitCents: 21998, selections: approvedSelections.map(([label, value]) => ({ label, value })), selectionsMatchedByValueOnly: true,
      seelTier: { actualVariantId: String(seel.variant_id), actualOriginalUnitCents: seel.original_price, actualFinalUnitCents: seel.final_price,
        actualFinalLineCents: seel.final_line_price, quantity: seel.quantity },
      effectiveNoteAttributesBound: false, supplierHookExecuted: false, checkoutCreationAllowed: false, trustedStorefrontCartEstablished: false
    };
    b03JReceiptBindings.set(receipt, binding);
    return { captureOnly: true, receipt };
  };
  const b03JAssertReceipt = receipt => {
    const binding = b03JReceiptBindings.get(receipt);
    if (!binding) b03JBlock('SOURCE_CHANGED');
    b03JAssertBinding(binding);
  };
  // Future submission comparator: only a separately reviewed post-hook payload digest may bind it.
  // This capture candidate never calls it from a creation path or supplies an approved digest.
  const b03JVerifyPostHookNoteAttributes = async (payload, approved, sha256) => {
    if (!approved || approved.phase !== 'post-hook-actual-payload' || !Number.isSafeInteger(approved.entryCount)
      || !Number.isSafeInteger(approved.canonicalUtf8Bytes) || !/^[0-9a-f]{64}$/.test(approved.sha256 || '')) b03JBlock('FINAL_BINDING_MISSING');
    const actual = b03JData(payload, 'note_attributes');
    const canonical = b03JEffectiveCanonical(actual);
    if (actual.length !== approved.entryCount || new TextEncoder().encode(canonical).byteLength !== approved.canonicalUtf8Bytes
      || await b03JDigest(canonical, 65536, sha256) !== approved.sha256
      || b03JEffectiveCanonical(b03JData(payload, 'note_attributes')) !== canonical) b03JBlock('FINAL_BINDING_MISMATCH');
    return true;
  };

  const b03HEnvelopeFlags = cart => {
    const cartPresent = Boolean(cart);
    return Object.freeze({
      cartPresent,
      tokenIsNonemptyString: cartPresent && typeof cart.token === 'string' && Boolean(cart.token),
      currencyIsUSD: cartPresent && cart.currency === 'USD',
      noteIsEmpty: cartPresent && String(cart.note || '') === '',
      attributesAreEmpty: cartPresent && Object.keys(cart.attributes || {}).length === 0,
      itemCountIsThree: cartPresent && cart.items?.length === 3
    });
  };
  const b03EIsCaptureQuery = () => new URLSearchParams(location.search).get('fd_b03_c_merge') === 'b03-c-20260927-capture';
  const b03EDiagnosticWindow = () => {
    const expiry = Date.parse(b03CMergeScope.expiresAtUtc || '');
    return b03EIsCaptureQuery()
      && b03CMergeScope.enabled === false && b03CMergeScope.captureEnabled === true
      && Number.isFinite(expiry) && Date.now() < expiry
      && true
      && window.Shopify && window.Shopify.shop === 'c5e1ec-d7.myshopify.com'
      && location.hostname === 'www.fredesigner.com' && location.pathname === '/cart';
  };
  const b03EAllowedStages = new Set(['cart-read', 'html-evidence', 'configured-pricing', 'scope', 'capture', 'UNCLASSIFIED']);
  const b03EAllowedCodes = new Set([
    'READ_HTTP_FAILURE', 'READ_ABORTED', 'CART_RESPONSE_INVALID', 'HTML_EVIDENCE_MISSING',
    'CONFIGURED_PRICING_INVALID', 'NO_CUSTOMIZED_LINE', 'SCOPE_DISABLED', 'SCOPE_WINDOW',
    'SCOPE_TARGET', 'SCOPE_BINDINGS', 'SCOPE_ENVELOPE', 'SCOPE_VARIANTS', 'SCOPE_QUANTITY',
    'SCOPE_LINE_IDENTITY', 'SCOPE_PRICING', 'SCOPE_SELECTIONS', 'SCOPE_SNAPSHOT',
    'SCOPE_SHIPPING', 'CAPTURE_RAW_BINDING', 'CAPTURE_NOTE_ATTRIBUTES', 'CAPTURE_UNKNOWN_PENDING', 'CAPTURE_PENDING_UNREADABLE', 'CAPTURE_HASH', 'CAPTURE_SOURCE_CHANGED', 'CART_CHANGED', 'UNCLASSIFIED'
  ]);
  const b03EExactErrorCodes = Object.freeze({
    'B03-J capture blocked: WINDOW': ['scope', 'SCOPE_WINDOW'],
    'B03-J capture blocked: UNKNOWN_PENDING': ['capture', 'CAPTURE_UNKNOWN_PENDING'],
    'B03-J capture blocked: PENDING_UNREADABLE': ['capture', 'CAPTURE_PENDING_UNREADABLE'],
    'B03-J capture blocked: RAW_BINDING': ['capture', 'CAPTURE_RAW_BINDING'],
    'B03-J capture blocked: INPUT_UNSUPPORTED': ['capture', 'CAPTURE_NOTE_ATTRIBUTES'],
    'B03-J capture blocked: HASH_UNAVAILABLE': ['capture', 'CAPTURE_HASH'],
    'B03-J capture blocked: SOURCE_CHANGED': ['capture', 'CAPTURE_SOURCE_CHANGED'],
    'Cart response was invalid.': ['cart-read', 'CART_RESPONSE_INVALID'],
    'Cart server evidence was missing.': ['html-evidence', 'HTML_EVIDENCE_MISSING'],
    'Configured cart pricing did not pass validation.': ['configured-pricing', 'CONFIGURED_PRICING_INVALID'],
    'B03-C merge experiment blocked: no validated customized line; native checkout is disabled for this experiment.': ['configured-pricing', 'NO_CUSTOMIZED_LINE'],
    'B03-C merge experiment blocked: the experiment is not enabled': ['scope', 'SCOPE_DISABLED'],
    'B03-C merge experiment blocked: General price-based shipping warning has not been reviewed': ['scope', 'SCOPE_SHIPPING'],
    'B03-C merge experiment blocked: the approved experiment window expired or is missing': ['scope', 'SCOPE_WINDOW'],
    'B03-C merge experiment blocked: store, draft, host or original cart page differs': ['scope', 'SCOPE_TARGET'],
    'B03-C merge experiment blocked: trusted original-cart bindings are missing': ['scope', 'SCOPE_BINDINGS'],
    'B03-C merge experiment blocked: cart envelope differs': ['scope', 'SCOPE_ENVELOPE'],
    'B03-C merge experiment blocked: duplicate or extra variant': ['scope', 'SCOPE_VARIANTS'],
    'B03-C merge experiment blocked: Bolonia quantity differs': ['scope', 'SCOPE_QUANTITY'],
    'B03-C merge experiment blocked: variant, quantity, price, shipping or Seel allocation differs': ['scope', 'SCOPE_LINE_IDENTITY'],
    'B03-C merge experiment blocked: configured pricing evidence differs': ['scope', 'SCOPE_PRICING'],
    'B03-C merge experiment blocked: one of the seven approved product values differs': ['scope', 'SCOPE_SELECTIONS'],
    'B03-C merge experiment blocked: original cart or approved snapshot no longer matches': ['scope', 'SCOPE_SNAPSHOT'],
    'Your cart changed while checkout was being prepared. Review your cart and try again.': ['capture', 'CART_CHANGED']
  });
  const b03ESafeDiagnostic = (error, stage = 'UNCLASSIFIED', configured) => {
    if (error && typeof error === 'object' && b03EDiagnosticRecords.has(error)) return b03EDiagnosticRecords.get(error);
    let message = '', name = '';
    try {
      message = typeof (error && error.message) === 'string' ? error.message : '';
      name = typeof (error && error.name) === 'string' ? error.name : '';
    } catch (_) {}
    const exact = Object.prototype.hasOwnProperty.call(b03EExactErrorCodes, message) ? b03EExactErrorCodes[message] : null;
    const diagnostic = { stage: b03EAllowedStages.has(stage) ? stage : 'UNCLASSIFIED', code: 'UNCLASSIFIED' };
    if (exact) { diagnostic.stage = exact[0]; diagnostic.code = exact[1]; }
    // Status is accepted only from an exact existing read-error format, never from raw error text.
    const http = message.match(/^(Cart request failed|Cart evidence request failed): ([1-5][0-9]{2})$/);
    if (http && ((stage === 'cart-read' && http[1] === 'Cart request failed')
      || (stage === 'html-evidence' && http[1] === 'Cart evidence request failed'))) {
      diagnostic.code = 'READ_HTTP_FAILURE';
      diagnostic.httpStatus = Number(http[2]);
    } else if (name === 'AbortError' && (stage === 'cart-read' || stage === 'html-evidence')) {
      diagnostic.code = 'READ_ABORTED';
    }
    if (configured && diagnostic.stage === 'configured-pricing') {
      const allowedValidation = new Set(['line-1', 'line-2', 'line-3', 'currency-line-1', 'currency-line-2', 'currency-line-3', 'configured-cart-total', 'cart-total']);
      diagnostic.validationErrors = Array.isArray(configured.validationErrors)
        ? configured.validationErrors.filter(value => typeof value === 'string' && allowedValidation.has(value)).slice(0, 8) : [];
      diagnostic.configuredFlags = {
        isValid: configured.isValid === true,
        hasCurrencyMismatch: configured.hasCurrencyMismatch === true
      };
      const flags = ['isCustomized', 'lineHasCurrencyMismatch', 'serverRequiresCustomization', 'serverRequiresConfiguredPriceState', 'serverBcpoDataPresent', 'guardedColorPriceRequired', 'guardedColorPriceIsValid'];
      diagnostic.lineFlags = configured.lines instanceof Map ? Array.from(configured.lines.values()).slice(0, 3).map((line, index) => {
        const result = { line: index + 1 };
        flags.forEach(key => { result[key] = line && typeof line[key] === 'boolean' ? line[key] : null; });
        return result;
      }) : [];
    }
    if (diagnostic.stage === 'scope' && diagnostic.code === 'SCOPE_ENVELOPE'
      && b03EDiagnosticWindow() && error && b03HEnvelopeRecords.has(error)) {
      const flags = b03HEnvelopeRecords.get(error);
      const names = ['cartPresent', 'tokenIsNonemptyString', 'currencyIsUSD', 'noteIsEmpty', 'attributesAreEmpty', 'itemCountIsThree'];
      diagnostic.envelopeFlags = {};
      names.forEach(name => { diagnostic.envelopeFlags[name] = typeof flags[name] === 'boolean' ? flags[name] : null; });
    }
    return diagnostic;
  };
  const b03EAttachFailure = (stage, error, configured) => {
    if (!b03EDiagnosticWindow()) return error;
    if (error && typeof error === 'object' && b03EDiagnosticRecords.has(error)) return error;
    const diagnostic = b03ESafeDiagnostic(error, stage, configured);
    const wrapped = new Error('B03-E diagnostic boundary');
    if (b03IAttributeRecords.has(error)) b03IAttributeRecords.set(wrapped, b03IAttributeRecords.get(error));
    if (diagnostic.code === 'CART_CHANGED') wrapped.cartChanged = true;
    b03EDiagnosticRecords.set(wrapped, diagnostic);
    return wrapped;
  };
  const b03ERun = (stage, action) => {
    if (!b03EDiagnosticWindow()) return action();
    // Each rejection gets its own error-bound diagnostic; Promise.all has no shared stage variable.
    return Promise.resolve().then(action).catch(error => { throw b03EAttachFailure(stage, error); });
  };
  const b03EVisibleFailure = diagnostic => {
    if (!b03EDiagnosticWindow() || !b03EAllowedStages.has(diagnostic.stage) || !b03EAllowedCodes.has(diagnostic.code)) return;
    const previous = document.getElementById('fd-b03-e-visible-failure');
    if (previous) previous.remove();
    const panel = document.createElement('pre');
    panel.id = 'fd-b03-e-visible-failure';
    panel.textContent = JSON.stringify(diagnostic, null, 2);
    (document.querySelector('form[action*="/cart"]') || document.body).appendChild(panel);
  };

  const startCheckout = (context) => {
    if (checkoutNavigating) return checkoutFlight;
    if (checkoutFlight) return checkoutFlight;
    try { validateCheckoutControls(context); }
    catch (error) {
      showCheckoutError(b03EIsCaptureQuery() ? CHECKOUT_ERROR_TEXT : error.message, context);
      if (b03EDiagnosticWindow()) b03EVisibleFailure(b03ESafeDiagnostic(error));
      return Promise.resolve();
    }
    const coordinator = window.__fdCartCoordinator;
    if (!coordinator) {
      showCheckoutError('Please refresh the page before checking out. Your cart is saved.', context);
      return Promise.resolve();
    }
    savePreparation(context && context.resumeCount || 0);
    coordinator.lock();
    const restoreBusy = setCheckoutBusy();
    let controlsRestored = false;
    const restoreControls = () => { if (!controlsRestored) { restoreBusy(); coordinator.unlock(); controlsRestored = true; } };
    clearCheckoutError();
    document.documentElement.dataset.fdBcpoCheckoutState = 'validating-cart';

    const operation = (async () => {
      let requestSent = false;
      let confirmedCheckoutAvailable = false;
      const b03COptIn = new URLSearchParams(location.search).get('fd_b03_c_merge');
      const preparationDeadline = Date.now() + 15000;
      const attempt = async () => {
        const b03JEntry = b03COptIn === 'b03-c-20260927-capture' && b03JCaptureEnabled ? b03JEntryContext(coordinator) : null;
        await coordinator.waitForIdle(CHECKOUT_TIMEOUT_MS);
        if (b03JEntry) b03JAssertEntry(b03JEntry);
        const cartRevision = coordinator.revision;
        const [cart, serverEvidenceByLineKey] = await Promise.all([
          b03ERun('cart-read', getCart),
          b03ERun('html-evidence', getFreshServerCartEvidence)
        ]);
        if (b03JEntry) b03JAssertEntry(b03JEntry);
        if (b03COptIn === 'b03-c-20260927-capture' && b03JCaptureEnabled) b03JPreflightCart(cart);
        const signature = cartSignature(cart);
        const fieldsSignature = formSignature();
        const assertCurrent = () => {
          if (coordinator.pendingCount() || coordinator.revision !== cartRevision || formSignature() !== fieldsSignature) {
            const error = new Error('Your cart changed while checkout was being prepared. Review your cart and try again.');
            error.cartChanged = true;
            throw error;
          }
        };
        const verifyCurrentCart = async () => {
          assertCurrent();
          const current = await b03ERun('cart-read', getCart);
          assertCurrent();
          if (cartSignature(current) !== signature) {
            const error = new Error('Your cart changed while checkout was being prepared. Review your cart and try again.');
            error.cartChanged = true;
            throw error;
          }
        };
        assertCurrent();
        if (b03EDiagnosticWindow()) await b03ERun('configured-pricing', () => applyCart(cart, serverEvidenceByLineKey));
        else applyCart(cart, serverEvidenceByLineKey);
        const configured = activeConfiguredCart;
        if (!configured || !configured.isValid) {
          throw b03EAttachFailure('configured-pricing', new Error('Configured cart pricing did not pass validation.'), configured);
        }

        const boloniaQuantity = boloniaReviewQuantity(cart, configured);
        if (boloniaQuantity) {
          clearPreparation();
          if (boloniaHasExistingVoCheckout(cart)) {
            showCheckoutError('An earlier custom checkout exists or is still unconfirmed. Please contact us to resolve that checkout before starting this fixed test. No replacement checkout has been created.', context);
            return;
          }
          await verifyCurrentCart();
          readCurrencyContext(cart);
          showCheckoutError('This cart matches the merchant-authorized fixed Bolonia test. Your cart will not be imported. On the next page, review all seven options and select quantity ' + boloniaQuantity + ' again. Only the authorized test customer can continue. No order is created by opening the review page.', context);
          document.querySelectorAll('[data-fd-bcpo-checkout-error]').forEach((notice) => {
            if (notice.hidden) return;
            const link = document.createElement('a');
            link.href = BOLONIA_REVIEW_URL;
            link.textContent = 'Review fixed Bolonia test configuration';
            link.rel = 'noreferrer';
            link.style.display = 'block';
            link.style.marginTop = '8px';
            let navigating = false;
            link.addEventListener('click', async (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              if (navigating) return;
              navigating = true;
              try {
                await verifyCurrentCart();
                readCurrencyContext(cart);
                if (boloniaHasExistingVoCheckout(cart)) throw new Error('An earlier custom checkout needs review. No replacement checkout has been created.');
                window.location.assign(BOLONIA_REVIEW_URL);
              } catch (error) {
                navigating = false;
                showCheckoutError(error.message || CHECKOUT_ERROR_TEXT, context);
              }
            });
            notice.appendChild(link);
          });
          document.documentElement.dataset.fdBcpoCheckoutState = 'controlled-test-review';
          return;
        }

        if (configured.customizedItemCount === 0) {
          if (b03COptIn === 'b03-c-20260927-capture' || b03COptIn === 'b03-c-20260927') {
            throw b03EAttachFailure('configured-pricing', new Error('B03-C merge experiment blocked: no validated customized line; native checkout is disabled for this experiment.'), configured);
          }
          if (readPendingRecord()) {
            checkoutResultUncertain = true;
            throw new Error(UNCERTAIN_CHECKOUT_ERROR_TEXT);
          }
          if (context && context.forceConfigured) {
            throw new Error('The custom checkout control did not match the refreshed cart.');
          }
          await verifyCurrentCart();
          document.documentElement.dataset.fdBcpoCheckoutState = 'verified-native';
          clearPreparation();
          checkoutNavigating = true;
          restoreControls();
          continueNativeCheckout(context);
          return;
        }

        const currencyContext = readCurrencyContext(cart);
        const supplierModeFingerprint = b03COptIn === 'b03-c-20260927'
          ? 'requires_shipping=true:' + await b03CSha256(JSON.stringify(b03CMergeScope)) : 'ordinary';
        if (b03COptIn === 'b03-c-20260927') {
          await supplierMergeExperiment({ cart }, {
            optIn: b03COptIn, now: Date.now(), shop: window.Shopify && window.Shopify.shop,
            themeId: window.Shopify && window.Shopify.theme && window.Shopify.theme.id,
            host: location.hostname, path: location.pathname, configured,
            cartSignature: signature, fieldsSignature, sha256: b03CSha256
          }, b03CMergeScope);
          assertCurrent();
        }
        if (b03COptIn === 'b03-c-20260927-capture') {
          try {
          const capture = await supplierMergeExperiment({ cart }, {
            optIn: 'b03-c-20260927-capture', now: Date.now(),
            shop: window.Shopify && window.Shopify.shop,
            themeId: window.Shopify && window.Shopify.theme && window.Shopify.theme.id,
            host: location.hostname, path: location.pathname,
            configured, cartSignature: signature, fieldsSignature, assertCurrent, b03JEntry,
            sha256: b03CSha256
          }, b03CMergeScope);
          await verifyCurrentCart();
          assertCurrent();
          if (b03JCaptureEnabled) b03JAssertReceipt(capture.receipt);
          const previous = document.getElementById('fd-b03-c-visible-digests');
          if (previous) previous.remove();
          const panel = document.createElement('pre');
          panel.id = 'fd-b03-c-visible-digests';
          panel.textContent = JSON.stringify(capture.receipt, null, 2);
          (document.querySelector('form[action*="/cart"]') || document.body).appendChild(panel);
          clearPreparation();
          document.documentElement.dataset.fdBcpoCheckoutState = 'b03-c-capture-only';
          return;
          } catch (error) {
            if (b03JCaptureEnabled && error && error.cartChanged) error = new Error('B03-J capture blocked: SOURCE_CHANGED');
            throw b03EAttachFailure('capture', error);
          }
        }
        const recoveredUrl = recoverConfirmedCheckout(cart, signature, fieldsSignature, currencyContext, configured, supplierModeFingerprint);
        if (recoveredUrl) {
          confirmedCheckoutAvailable = true;
          // Recovery repeats only reads, never checkout creation. Revalidate the
          // cart and visible fields before reusing the already-confirmed URL.
          await verifyCurrentCart();
          readCurrencyContext(cart, currencyContext);
          clearPreparation();
          clearPendingCheckout(readConfirmedCheckout() && readConfirmedCheckout().localAttemptId);
          checkoutResultUncertain = false;
          document.documentElement.dataset.fdBcpoCheckoutState = 'redirecting-recovered';
          checkoutNavigating = true;
          window.location.assign(recoveredUrl);
          return;
        }
        if (readPendingRecord()) {
          checkoutResultUncertain = true;
          throw new Error(UNCERTAIN_CHECKOUT_ERROR_TEXT);
        }
        checkoutResultUncertain = false;
        const beforeHookCart = cartSignature(cart);
        const bcpoApi = await waitForBcpo();
        readCurrencyContext(cart, currencyContext);
        triggerBeforeCheckout(bcpoApi, cart);
        if (cartSignature(cart) !== beforeHookCart) throw new Error('The cart changed during checkout validation.');
        await verifyCurrentCart();
        readCurrencyContext(cart, currencyContext);
        const payload = buildBcpoPayload(cart, bcpoApi, currencyContext);
        if (b03COptIn === 'b03-c-20260927') {
          await supplierMergeExperiment(payload, {
          optIn: b03COptIn, now: Date.now(),
          shop: window.Shopify && window.Shopify.shop,
          themeId: window.Shopify && window.Shopify.theme && window.Shopify.theme.id,
          host: location.hostname, path: location.pathname,
          configured, cartSignature: signature, fieldsSignature,
          sha256: b03CSha256
          }, b03CMergeScope);
          await verifyCurrentCart();
        }
        assertCurrent();
        document.documentElement.dataset.fdBcpoCheckoutState = 'confirming-addons';
        // Stop reload recovery BEFORE a checkout-creation request can be sent.
        clearPreparation();
        const localAttemptId = crypto.randomUUID();
        requestSent = true;
        savePendingCheckout(signature, supplierModeFingerprint, localAttemptId);
        const checkoutUrl = await requestBcpoCheckout(payload, bcpoApi, configured);
        // Save a validated response before any subsequent read or navigation
        // can fail. Never put a guessed URL or an unconfirmed response here.
        saveConfirmedCheckout({
          supplierModeFingerprint, localAttemptId,
          time: Date.now(), signature, fieldsSignature, cartToken: String(cart.token || ''),
          customerId: checkoutCustomerId(), currency: currencyContext.currency, rate: currencyContext.rate,
          configuredSubtotalCents: configured.configuredSubtotalCents, url: checkoutUrl
        });
        confirmedCheckoutAvailable = true;
        await verifyCurrentCart();
        readCurrencyContext(cart, currencyContext);
        clearPendingCheckout(localAttemptId);
        document.documentElement.dataset.fdBcpoCheckoutState = 'redirecting';
        checkoutNavigating = true;
        window.location.assign(checkoutUrl);
      };
      try {
        for (let attemptNumber = 0; ; attemptNumber++) {
          try { await attempt(); break; }
          catch (error) {
            if (b03JCaptureEnabled && b03COptIn === 'b03-c-20260927-capture' && error && error.cartChanged) error = new Error('B03-J capture blocked: SOURCE_CHANGED');
            // Only repeat read-only preparation. Never replay a sent create request.
            if (!requestSent && error.cartChanged && attemptNumber < 2 && Date.now() < preparationDeadline) continue;
            throw error;
          }
        }
      } catch (error) {
        if (leavingCartDocument && !requestSent) return;
        clearPreparation();
        if (requestSent) checkoutResultUncertain = true;
        checkoutNavigating = false;
        const b03EDiagnostic = b03EDiagnosticWindow()
          ? b03IAttributeFingerprintEnabled ? await b03IReadAttributeDiagnostic(error) : b03ESafeDiagnostic(error)
          : null;
        document.documentElement.dataset.fdBcpoCheckoutFailureStatus = b03EIsCaptureQuery()
          ? b03EDiagnostic ? b03EDiagnostic.code : 'capture-failed'
          : String(error && (error.status || error.message) || 'failed');
        const checkoutErrorText = error && error.cartChanged ? error.message
          : error && error.currencyContextChanged ? CURRENCY_CONTEXT_ERROR_TEXT
          : confirmedCheckoutAvailable ? CONFIRMED_CHECKOUT_RETRY_TEXT
          : checkoutResultUncertain ? UNCERTAIN_CHECKOUT_ERROR_TEXT
          : activeConfiguredCart && activeConfiguredCart.hasCurrencyMismatch
          ? CURRENCY_CHANGE_ERROR_TEXT.replace('{currency}', activeConfiguredCart.currency)
          : CHECKOUT_ERROR_TEXT;
        showCheckoutError(b03EIsCaptureQuery() ? CHECKOUT_ERROR_TEXT : checkoutErrorText, context);
        if (b03EDiagnostic) b03EVisibleFailure(b03EDiagnostic);
      } finally {
        if (!checkoutNavigating) restoreControls();
      }
    })();

    checkoutFlight = operation.finally(() => {
      checkoutFlight = null;
      if (b08PriceStateEnabled()) b08SyncCheckout();
    });
    return checkoutFlight;
  };

  const applyCartUpdate = (cart) => {
    if (b08PriceStateEnabled()) { b08InvalidatePrices(); b08RefreshPrice(); return; }
    applyCart(cart);
  };

  const captureCheckout = (event, context) => {
    if (!context) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (b08PriceStateEnabled() && b08PricePhase !== 'ready') {
      showCheckoutError('Your custom price is not confirmed. Refresh the price before checkout.', context);
      return;
    }
    startCheckout(context);
  };
  // Run before document-level app handlers can start an unvalidated checkout.
  window.addEventListener('click', (event) => captureCheckout(event, getCheckoutClickContext(event)), true);
  window.addEventListener('submit', (event) => captureCheckout(event, getCheckoutSubmitContext(event)), true);
  // Liquid leaves these controls inert if either required script fails to load.
  const enableGuardedCheckout = () => {
    if (b08PriceStateEnabled()) { b08SyncCheckout(); return; }
    if (!window.__fdCartCoordinator) return;
    document.querySelectorAll('[data-fd-checkout-guarded]').forEach((control) => {
      control.disabled = false;
      control.removeAttribute('data-fd-checkout-guarded');
    });
    document.querySelectorAll('[data-fd-checkout-loading]').forEach((notice) => { notice.hidden = true; });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enableGuardedCheckout, { once: true });
  else enableGuardedCheckout();
  // Seel can reload /cart after replacing its protection line. Resume only a
  // recent, explicitly requested preparation that never sent a create request.
  // At most two reloads; back/forward navigation never triggers this recovery.
  const resumePreparation = () => {
    if (b08PriceStateEnabled() && b08PricePhase !== 'ready') return;
    const saved = readPreparation();
    if (!saved) return;
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation || navigation.type !== 'reload' || saved.path !== location.pathname
      || Date.now() - saved.time > 20000 || saved.resumeCount >= 2 || readPendingCheckout()) {
      clearPreparation();
      return;
    }
    const control = document.querySelector('[data-fd-cart-page] [name="checkout"]');
    const form = control && control.form;
    if (!isCartForm(form)) { clearPreparation(); return; }
    const fields = Array.from(document.querySelectorAll('[name="note"], [name^="attributes["]'));
    (saved.fields || []).forEach(field => {
      const index = fields.findIndex(c => c.name === field.name);
      if (index < 0) return;
      const input = fields.splice(index, 1)[0];
      input.value = field.value;
      if (typeof field.checked === 'boolean') input.checked = field.checked;
    });
    startCheckout({ control, form, resumeCount: saved.resumeCount + 1 });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumePreparation, { once: true });
  else window.setTimeout(resumePreparation, 0);

  // Back/forward cache restores the previous DOM, including disabled controls.
  window.addEventListener?.('pageshow', (event) => { if (event.persisted) window.location.reload(); });

  document.addEventListener('cart:updated', (event) => applyCartUpdate(event.detail && event.detail.cart));
  const isCartPage = () => Boolean(document.querySelector('[data-fd-cart-page]'))
    || /\/cart\/?$/.test(window.location.pathname);

  const run = () => {
    if (b08PriceStateEnabled()) {
      b08InstallPriceUI();
      if (!b08InitialPriceRead) { b08InitialPriceRead = true; b08RefreshPrice(); }
      return;
    }
    if (window.__fdCartSnapshot) {
      applyCartUpdate(window.__fdCartSnapshot);
      return;
    }
    if (!isCartPage()) return;

    if (hasAppliedInitialCart || window.__fdCartSnapshot) {
      if (!hasAppliedInitialCart && window.__fdCartSnapshot) applyCartUpdate(window.__fdCartSnapshot);
      return;
    }
    const initialCartRequest = typeof window.__fdGetCartShared === 'function'
      ? window.__fdGetCartShared()
      : getCart();
    initialCartRequest.then(applyCartUpdate).catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
})();

